import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  FormControl,
  Tab,
  Autocomplete,
  Tabs,
  Box,
  Button,
  Typography,
  CircularProgress,
  Radio,
  FormControlLabel,
  RadioGroup,
  Divider,
  Alert,
  Backdrop,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemText,
  Chip, ButtonGroup, Menu, MenuItem, Stack, InputAdornment
} from "@mui/material";
import { CloseOutlined, AddOutlined, InfoOutlineRounded, ArrowDropDown } from '@mui/icons-material';
import { api } from "../services/api";
import { getAzureFormDefaults } from "../services/azureRequestHelpers";
import { getAzureVmFieldError, validateAzureVmFields } from "../services/azureVmValidation";
import { validateAzureVmCreation } from "../services/azureVmCreationValidation";
import Layout from "../layout/layout";
import { useToast } from "../components/ToastContext";
import { FormCheckbox, FormRadio } from "../components/ui";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import SaveTemplateDialog from '../pages/SaveTemplateDialog';
import GetAllTemplatesDialog from '../pages/GetAllTemplatesDialog';
import ListenersAlbAccordion from "../pages/alb/ListenersAlbAccordion";
import CommonTabs from "../components/ui/stepper";
import ProgressPopup from "../components/ui/progressPopup";
import RdsConfiguration from "./rds/rdsConfiguration";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RoleGuard from "../components/RoleGuard";
import {PERMISSIONS} from "../Utils/role";
import { showToast } from "../Utils/common";

import ListenersNLBAccordion from "../pages/alb/ListenersNLBAccordion";
import SecurityGroupRulesAccordion, {
  validateSecurityGroupRules,
  validateSGForNext,
  getSecurityGroupRulesPayload,
} from "../pages/sgrule/SecurityGroupRulesAccordion";


import DnsRecordForm from "../pages/alb/DnsRecordForm";
import { color } from "framer-motion";
import { applySystemReplacements, isValidAlbRedirectUrl } from "./CreateResource/utils/formHelpers";
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AzureSummary from "./Azure/AzureSummary";
import ResourcePricingCard from "../components/ResourcePricingCard";
import AzureStorageAccountPricing from "./Azure/AzureStorageAccountPricing";
import AzureVirtualMachinePricing from "./Azure/AzureVirtualMachinePricing";
import AzureStorageAccountCreation, {
  areStorageDropdownParentsReady,
  getAzureIpv4OrCidrError,
  getAzureStorageAccountNameError,
  getStorageFieldValue,
  getStorageCheckboxState,
  isAzureIpv4Field,
  isAzureStorageNameField,
  isStorageDataProtectionField,
  isStorageFieldVisible,
  normalizeStorageAccountFormConfig,
  validateAzureStorageAccountForm,
} from "./Azure/AzureStorageAccountCreation";
import AzureSubmissionProgressPopup from "../components/ui/AzureSubmissionProgressPopup";
import AzureVirtualMachineCreation from "./Azure/AzureVirtualMachineCreation";

const isCheckboxChecked = (value) =>
  value === true ||
  value === 1 ||
  (typeof value === "string" && value.toLowerCase() === "true");

const getPasswordError = (password) => {
  const str = String(password || "");
  if (!str) return "Master Password is required."; 
  if (str.length < 8) return "Minimum 8 characters required.";
  if (str.length > 128) return "Maximum 128 characters allowed.";
  if (/\s/.test(str)) return "Spaces are not allowed.";
  if (/[/'"@]/.test(str)) return "Cannot contain the symbols: / ' \" @";
  if (!/^[\x20-\x7E]+$/.test(str)) return "Only printable ASCII characters allowed.";
  return "";
};

const getUsernameError = (username) => {
  const str = String(username || "");
  if (!str) return "Master Username is required."; 
  
  if (str.length < 8 || str.length > 16) return "Username must be 8 to 16 characters long.";
  
  if (!/^[A-Za-z]/.test(str)) return "Username must start with a letter.";
  if (!/^[A-Za-z0-9_]+$/.test(str)) return "Only letters, numbers, and underscores are allowed.";
  
  return "";
};

const getResourceGroupNameError = (value) => {
  const name = String(value ?? "");

  if (name.length < 1) return "Resource group name is required.";
  if (name.length > 90) return "Maximum 90 characters allowed.";
  if (!/^[A-Za-z0-9_.()-]+$/.test(name)) {
    return "Use only letters, numbers, _, -, ., ( and ).";
  }
  if (name.endsWith(".")) {
    return "Resource group name cannot end with a period.";
  }

  return "";
};


const normalizeValueLabel = (value, label) => {
  if (value && typeof value === "object" && "value" in value) {
    return {
      value: value.value,
      label: value.label ?? label ?? value.value,
    };
  }

  return {
    value,
    label: label ?? value,
  };
};

const formatPrice = (value, decimals = 2) => {
  if (value === null || value === undefined) return "N/A";

  return `$${Number(value).toFixed(decimals)}`;
};
const getDependentFields = (formConfig, fieldName) => {
  return formConfig.filter(field => {
    const parents = field.parentField?.split(",").map(p => p.trim()) || [];
    
    if (fieldName === "sqlServerEdition" && (field.fieldName === "engineVersion" || field.fieldName === "dbInstanceType")) {
        return true;
    }

    return parents.includes(fieldName) || field.dependentOn === fieldName;
  });
};

const extractDropdownApiData = (response) => {
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data?.result)) return response.data.result;
  return [];
};

const canFetchDropdownData = ({
  field,
  formValues,
  activeAccountId,
  subscriptionKey,
  userId,
  rootDropdownsFetchedRef,
  formConfig,
}) => {
  if (!field?.dropdownSource) {
    return false;
  }

  if (!field.parentField && rootDropdownsFetchedRef.current && !field.dependentOn) {
    return false;
  }

  if (
    field.parentField &&
    !areParentsReady({
      field,
      formValues,
      subscriptionKey,
      activeAccountId,
      userId,
      formConfig,
    })
  ) {
    return false;
  }

  return true;
};

const processDependentField = ({
  field,
  triggeringField,
  normalizedValue,
  newData,
  dropdownDependencyCache,
  setFormConfig,
  setDropdownData,
  fetchDropdownData,
}) => {
  let shouldDisplay = true;

  if (field.dependentOn) {
    if (field.fieldName === "GoldenImage") {
      shouldDisplay = newData.TemplateOptions?.value === 2;
    } else if (field.dependentValue) {
      const rawDepValue = newData[field.dependentOn];
      const depValue = rawDepValue?.value !== undefined ? rawDepValue.value : rawDepValue;

      const expectedValues = String(field.dependentValue)
        .split(',')
        .map(v => v.trim().toLowerCase());
        
      shouldDisplay = expectedValues.includes(String(depValue ?? "").toLowerCase());
    } else {
      shouldDisplay = Boolean(newData[field.dependentOn]?.value);
    }
  }

  updateFieldVisibility(setFormConfig, field.fieldName, shouldDisplay);

  if (!shouldDisplay || !normalizedValue || normalizedValue === "") {
    newData[field.fieldName] = field.fieldType === "multiSelectDropdown" 
      ? { value: [], label: "" } 
      : { value: "", label: "" };

    setDropdownData(prev => ({ ...prev, [field.fieldName]: [] }));

    const cacheKeys = Object.keys(dropdownDependencyCache.current);
    cacheKeys.forEach(key => {
      if (key.startsWith(`${field.fieldName}::`)) {
        delete dropdownDependencyCache.current[key];
      }
    });
    return;
  }

  newData[field.fieldName] = field.fieldType === "multiSelectDropdown" 
    ? { value: [], label: "" } 
    : { value: "", label: "" };

  const parents = field.parentField?.split(",").map(p => p.trim()) ?? [];
  const dependencyKey = `${field.fieldName}::${parents.length > 0 ? parents.map(p => newData[p]?.value).join("|") : normalizedValue}`;

  if (dropdownDependencyCache.current[dependencyKey]) {
    return;
  }

  dropdownDependencyCache.current[dependencyKey] = true;
  
  fetchDropdownData(field.fieldName, newData);
};

const SYSTEM_PARENTS = new Set([
  "subscriptionKey",
  "accountId",
  "ActiveAccountID",
  "ResourceType",
  "userId",
]);

const isSystemParentCheck = (parent) => SYSTEM_PARENTS.has(parent);

const resolveParentValue = (rawValue) => {
  if (Array.isArray(rawValue)) {
    const v = rawValue[0];
    return v?.value ?? v?.label ?? v?.text ?? null;
  }

  if (typeof rawValue === "object" && rawValue !== null) {
    return rawValue.value ?? rawValue.label ?? rawValue.text ?? null;
  }

  return rawValue ?? null;
};

const buildDropdownUrl = ({
  field,
  formValues,
  activeAccountId,
  subscriptionKey,
  userId,
  resourceType,
  formConfig 
}) => {
  let url = applySystemReplacements(field.dropdownSource, {
    activeAccountId,
    subscriptionKey,
    userId,
    resourceType,
  });

  if (
    field.fieldName === "HostedZones" &&
    activeAccountId &&
    !url.includes("activeAccountId=")
  ) {
    const separator = url.includes("?") ? "&" : "?";
    url = `${url}${separator}activeAccountId=${encodeURIComponent(activeAccountId)}`;
  }

  if (url.includes("sqlServerEdition")) {
    const sqlEdition = formValues["sqlServerEdition"]?.value || "";
    url = url.replace("{sqlServerEdition}", sqlEdition);
    
    if (url.includes("{sqlServerEd")) {
      url = url.replace("{sqlServerEd", sqlEdition);
    }
  }

  if (!field.parentField) {
    return url;
  }

  const parents = field.parentField.split(",").map(p => p.trim());

  for (const parent of parents) {
    if (isSystemParentCheck(parent)) {
      continue;
    }

    const parentFieldConfig = formConfig?.find(f => f.fieldName === parent);
    const isHidden = parentFieldConfig ? isFieldHidden(parentFieldConfig, formValues) : false;

    const rawParentValue = formValues[parent]?.value;
    const parentValue = resolveParentValue(rawParentValue);

    if (!parentValue && !isHidden) {
      return null; 
    }

    url = url.replace(
      `{${parent}}`,
      parent === "platformDetails"
        ? encodeURIComponent(parentValue || "")
        : (parentValue || "")
    );
  }

  return url;
};


//  Route53 helper (ADD HERE)
const getRoute53FullName = (formData) => {
  return formData.RecordName?.value || "";
};

const renderSafeText = (v) => {
  if (v == null) return "";
  if (typeof v === "object") {
    return v.label ?? v.text ?? v.value ?? "";
  }
  return String(v);
};

const acquireDropdownLock = (dropdownRequestRef, fieldName) => {
  if (dropdownRequestRef.current[fieldName]) return false;
  dropdownRequestRef.current[fieldName] = true;
  return true;
};

const releaseDropdownLock = (dropdownRequestRef, fieldName) => {
  dropdownRequestRef.current[fieldName] = false;
};

const renderAwsS3Pricing = (awsPrice, organizationName) => {
  const appliedName = organizationName || "Gainwell Technologies";

  return (
    <Stack spacing={2.5}>
      {/* Standard Pricing Section */}


 <Box sx={{
        border: 1,
        borderColor: (t) => t.palette.mode === 'dark' ? 'divider' : '#d9e8f6',
        borderRadius: 2,
        bgcolor: (t) => t.palette.mode === 'dark' ? 'background.default' : '#ffffffb4'
      }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            bgcolor: (t) => t.palette.mode === 'dark' ? 'var(--app-bg-soft)' : '#d9e8f6',
            borderRadius: "8px 8px 2px 2px",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {/* Discount Applied */}
                Forecasted Price
        
            </Typography>

            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              ({appliedName})
            </Typography>
          </Box>

        </Box>

        {/* Prices */}
        <Box sx={{ px: 1.5, py: 1.5 }}>
          <Box sx={{ mb: 1 }}>
            {/* Updated Label */}
            <Typography sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}>
               Price Per GB/Month
             </Typography>

          
             <Typography
              component="span"
              variant="h5"
              sx={{ fontWeight: 700, color: (t) => t.palette.mode === 'dark' ? '#81c784' : '#0d7a46', fontSize: "1.5rem",}}
            >
               {formatPrice(Number(renderSafeText(awsPrice.pricePerHr)))}
            </Typography>
             {/* <Typography
              component="span"
              variant="body1"
              sx={{ color: "text.secondary", ml: 0.5 }}
            >
              / month
            </Typography>  */}
          </Box>

        </Box>
      </Box>
    


      <Box>

      </Box>
    </Stack>
  );
};

const renderAwsStandardPricing = (awsPrice, organizationName) => {
  return <ResourcePricingCard price={awsPrice} organizationName={organizationName} />;
};

const renderAzurePricing = (priceDetails, clientName) => {
  const appliedName = clientName || "Gainwell Technologies";


  const marketPerHr = priceDetails?.marketRatePerHr ?? 0.19;
  const marketPerMonth = priceDetails?.marketRatePerMonth ?? 140.16;
  const discountPerHr = priceDetails?.discountPricePerHr ?? 0.13;
  const discountPerMonth = priceDetails?.discountPricePerMonth ?? 97.82;

  return (
    <Stack spacing={2.5}>
      {/* Standard Pricing Section */}
      <Box>
        <Typography variant="body2" color="text.secondary">
          Forecasted Price Per Hour:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
          {formatPrice(marketPerHr)}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Forecasted Price Per Month:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {formatPrice(marketPerMonth)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "divider" }} />

      {/* Discounted Pricing Section */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}>
          Discount Applied 
          {/* ({appliedName}) */}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Typography component="span" variant="h5" sx={{ fontWeight: 700, color: (t) => t.palette.mode === "dark" ? "#81c784" : "#0d7a46" }}>
            {formatPrice(discountPerHr)}
          </Typography>
          <Typography component="span" variant="body1" sx={{ color: "text.secondary", ml: 0.5 }}>
            / Hour
          </Typography>
        </Box>

        <Box>
          <Typography component="span" variant="h5" sx={{ fontWeight: 700, color: (t) => t.palette.mode === "dark" ? "#81c784" : "#0d7a46" }}>
            {formatPrice(discountPerMonth)}
          </Typography>
          <Typography component="span" variant="body1" sx={{ color: "text.secondary", ml: 0.5 }}>
            / Month
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};

const renderAwsPricing = (awsPrice, resourceName, organizationName) => {
  if (!awsPrice || !resourceName) return null;

  const type = resourceName.toLowerCase();
  const isS3 = type === "s3 bucket";
  // const isEc2 = type.includes("ec2");
  // const isNlb = type.includes("nlb");

  return isS3
    ? renderAwsS3Pricing(awsPrice, organizationName)
    : renderAwsStandardPricing(awsPrice, organizationName);
};

const renderPriceDetails = ({
  selectedProvider,
  priceDetails,
  awsPrice,
  resourceName,
  clientName,
  organizationName,
}) => {
  if (selectedProvider?.toUpperCase() === "AZURE" && priceDetails) {
    return renderAzurePricing(priceDetails, clientName);
  }
  if (selectedProvider?.toUpperCase() === "AWS" && awsPrice) {
    return renderAwsPricing(awsPrice, resourceName, organizationName);
  }
  return null;
};

const isSystemParentReady = ({
  parent,
  subscriptionKey,
  activeAccountId,
  userId,
}) => {
  if (parent === "subscriptionKey") return Boolean(subscriptionKey);
  if (parent === "accountId" || parent === "ActiveAccountID") return Boolean(activeAccountId);
  if (parent === "userId") return Boolean(userId);
  return true;
};

const isFieldHidden = (field, formData) => {
  if (!field.isDisplay) return true;

  if (field.dependentOn && field.dependentValue) {
    const rawParentValue = formData?.[field.dependentOn];
    const parentValue = rawParentValue?.value !== undefined ? rawParentValue.value : rawParentValue;

    const expectedValues = String(field.dependentValue).split(',');

    if (!expectedValues.includes(String(parentValue))) {
      return true;
    }
  }

  return false;
};

const isFormParentReady = (formValues, parent, formConfig) => {
  const parentFieldConfig = formConfig.find(f => f.fieldName === parent);

  if (parentFieldConfig && isFieldHidden(parentFieldConfig, formValues)) {
    return true;
  }

  return Boolean(formValues[parent]?.value);
};

const updateFieldVisibility = (setFormConfig, fieldName, isDisplay) => {
  setFormConfig(prev => {
    const field = prev.find(f => f.fieldName === fieldName);
    if (field && field.isDisplay === isDisplay) {
      return prev;
    }

    return prev.map(f =>
      f.fieldName === fieldName ? { ...f, isDisplay } : f
    );
  });
};

const getOptionLabelGeneric = (option) => {
  if (option == null) return "";
  if (typeof option === "string") return option;
  return option.text ?? option.label ?? option.value ?? "";
};

const areParentsReady = ({
  field,
  formValues,
  subscriptionKey,
  activeAccountId,
  userId,
  formConfig,
}) => {
  if (!field.parentField) return true;

  const parents = field.parentField.split(",").map(p => p.trim());

  for (const parent of parents) {
    const isSystem = [
      "subscriptionKey",
      "accountId",
      "ActiveAccountID",
      "userId",
    ].includes(parent);

    if (isSystem) {
      if (!isSystemParentReady({ parent, subscriptionKey, activeAccountId, userId })) {
        return false;
      }
    } else if (!isFormParentReady(formValues, parent, formConfig)) {
      return false;
    }
  }

  return true;
};

const PasswordInputField = (props) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <TextField
      {...props}
      type={showPassword ? "text" : "password"}
      InputProps={{
        ...props.InputProps,
        endAdornment: (
          <InputAdornment
            position="end"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              edge="end"
              onMouseDown={(e) => e.preventDefault()}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
            {props.name === "masterPassword" && (
              <Tooltip
                title="Security Notice: Your password is wiped from memory immediately after the database is provisioned and will not be stored."
                placement="top"
                arrow
              >
                <InfoOutlinedIcon
                  style={{
                    color: 'red',
                    cursor: 'help',
                    fontSize: '1.25rem'
                  }}
                />
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}

const CreateResource = () => {
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const [formConfig, setFormConfig] = useState([]);
  const theme = useTheme();
  const toast = useToast();
  const [namingStandard, setNamingStandard] = useState('');
  const selectedProvider = localStorage.getItem("selectedProvider");
  const isAzure = selectedProvider?.toUpperCase() === "AZURE";
  const activeAccountId = isAzure
    ? (
      sessionStorage.getItem("activeAccountId") ||
      sessionStorage.getItem("selectedSubscription") ||
      localStorage.getItem("selectedSubscription") ||
      ""
    )
    : (
      localStorage.getItem("selectedAccountId") ||
      sessionStorage.getItem("activeAccountId") ||
      ""
    );

  const [formData, setFormData] = useState({});
  const [touchedVmFields, setTouchedVmFields] = useState({});
  const [dropdownData, setDropdownData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [dropdownLoading, setDropdownLoading] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTitle, setLoadingTitle] = useState("Loading Resource");
  const [loadingMessage, setLoadingMessage] = useState("Preparing your resource form...");
  const [loadingProgress, setLoadingProgress] = useState(20);
  const [setDiskPrice] = useState(null);
  const [setTemplatePrice] = useState(null);
  const [setVmPriceFetched] = useState(false);
  const [isManualResourceName] = useState(false);
  const [integrationError, setIntegrationError] = useState("");

  const [additionalTags, setAdditionalTags] = useState([]);
  const selectedProviderId = localStorage.getItem("selectedProviderId");
  const [awsPrice, setAwsPrice] = useState(null);
  const [storagePricingStatus, setStoragePricingStatus] = useState({ state: "idle" });
  const [vmPricingStatus, setVmPricingStatus] = useState({ state: "idle" });
  const navigate = useNavigate();

  // const [priceDetails, setPriceDetails] = useState(null);
  const [priceDetails, setPriceDetails] = useState({
  marketRatePerHr: 0.19,
  marketRatePerMonth: 140.16,
  discountPricePerHr: 0.13,
  discountPricePerMonth: 97.82,
});

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validatedTab, setValidatedTab] = useState(null);

  const location = useLocation();
  const { resourceId } = location.state || {};
  const { resourceName } = location.state || {};
  const templateId = location.state?.templateId;
  const isAzureStorageAccount =
    isAzure && resourceName?.toLowerCase().includes("storage account");
  const isAzureVirtualMachine =
    isAzure && resourceName?.toLowerCase().trim() === "virtual machine";
  const resourceType = isAzureStorageAccount
    ? "StorageAccount"
    : isAzureVirtualMachine ? "VirtualMachine" : "";
  const isTemplateFlow = Boolean(templateId);
  const [submitLoading, setSubmitLoading] = useState(false);
  const userId = sessionStorage.getItem("userId");
  const cloudProvider = localStorage.getItem("selectedProviderId");
  const [budgetLimit, setBudgetLimit] = useState(null);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const dropdownRequestRef = useRef({});
  const listenersRef = useRef(null);
  const dropdownDependencyCache = useRef({});
  const formConfigRequestKeyRef = useRef(null);
  const dropdownDataRef = useRef({});
  const loadedDropdownUrlRef = useRef({});
  const rootDropdownsFetchedRef = useRef(false);
  const previousFormDataRef = useRef({});
  const hasRedirectedToRulesRef = useRef(false);
  const pricingTimeoutRef = useRef(null);
  const MAX_TOTAL_TAGS = 50;
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [getAllTemplatesOpen, setGetAllTemplatesOpen] = useState(false);
  const [validationReason, setValidationReason] = useState("");
  const [isDuplicateNameValidation, setIsDuplicateNameValidation] = useState(false);
  const [openIntegrationDialog, setOpenIntegrationDialog] = useState(false);
  const [maxErrors, setMaxErrors] = useState({});
  const sidebarContext = location.state?.context;
  const [apiValidationFields, setApiValidationFields] = useState([]);
  const [apiFieldErrorText, setApiFieldErrorText] = useState("Please check this field");
  const [sgValid, setSgValid] = useState(false);
  const [sgNextError, setSgNextError] = useState("");
  const [sgTouched, setSgTouched] = useState(false);
  const [disabledFields, setDisabledFields] = useState({});

  // EDIT / CLONE Modes
  const mode = location.state?.mode; // "edit" | "clone"
  const editResourceId = location.state?.editResourceId;
  const clonedResource = location.state?.clonedResource === true;

  const azureValidationResourceId =
  mode === "edit" || mode === "clone" || clonedResource
    ? editResourceId
    : 0;



  const calculateTagStats = () => {
    const general = formConfig.filter(
      (f) => f.tabName === "Tag" && f.subTab === "General"
    ).length;

    const logicMonitor = formConfig.filter(
      (f) => f.tabName === "Tag" && f.subTab === "Logic Monitor"
    ).length;

    const optional = formConfig.filter(
      (f) => f.tabName === "Tag" && f.subTab === "Optional"
    ).length;

    const subtotal = general + logicMonitor + optional;
    const additionalAllowed = MAX_TOTAL_TAGS - subtotal;
    const currentAdditional = additionalTags.length;
    const remaining = additionalAllowed - currentAdditional;

    return {
      general,
      logicMonitor,
      optional,
      subtotal,
      additionalAllowed,
      currentAdditional,
      remaining,
      totalUsed: subtotal + currentAdditional,
    };
  };

  useEffect(() => {
    dropdownDataRef.current = dropdownData;
  }, [dropdownData]);

  useEffect(() => {
    if (formConfig.length > 0) {
      fetchDropdownData(null, formData);
    }
  }, [formConfig]);

  useEffect(() => {
    const isSecurityGroup = resourceName?.toLowerCase().includes("security");

    if (mode === "edit" && isSecurityGroup && formConfig.length > 0 && !hasRedirectedToRulesRef.current) {

      const dynamicTabNames = [...new Set(formConfig.map((field) => field.tabName))];
      const rulesTabIndex = dynamicTabNames.findIndex(name => name === "Rules");

      if (rulesTabIndex !== -1) {
        setActiveTab(rulesTabIndex);
        hasRedirectedToRulesRef.current = true;
      }
    }
  }, [mode, resourceName, formConfig]);

  useEffect(() => {
    if (!sidebarContext || !formConfig.length) return;

    const contextMapping = [
      { contextKey: "region", formField: "location" },
      { contextKey: "vpc", formField: "vpcId" },
      { contextKey: "subnet", formField: "subnet" },
      { contextKey: "securityGroup", formField: "securityGroupID" },
      { contextKey: "ec2Instance", formField: "Ec2instance" }
    ];

    let updatedAField = false;

    for (const { contextKey, formField } of contextMapping) {
      const incomingVal = sidebarContext[contextKey] || sidebarContext[formField];
      if (!incomingVal) continue;

      const currentVal = formData[formField]?.value;
      const availableOptions = dropdownData[formField] || [];

      //  WAIT for VPC before SG
      if (formField === "securityGroupID" && !formData.vpcId?.value) {
        break;
      }



      const matchedOption = availableOptions.find((opt) => {
        const valStr = String(opt?.value || "").toLowerCase();
        const textStr = String(opt?.text || opt?.label || "").toLowerCase();
        const targetStr = String(incomingVal).toLowerCase();

        return (
          valStr === targetStr ||
          textStr === targetStr ||
          textStr.includes(targetStr) ||
          targetStr.includes(valStr)
        );
      });

      //  value already set check
      const valueIsAlreadySet = (checkVal) => {
        if (currentVal == null) return false;

        if (Array.isArray(currentVal)) {
          return currentVal.some(v =>
            String(v).toLowerCase() === String(checkVal).toLowerCase()
          );
        }

        if (typeof currentVal === "object") {
          return String(currentVal.value || "").toLowerCase() === String(checkVal).toLowerCase();
        }

        return String(currentVal).toLowerCase() === String(checkVal).toLowerCase();
      };

      if (matchedOption && valueIsAlreadySet(matchedOption.value)) continue;
      if (valueIsAlreadySet(incomingVal)) continue;

      //  wait until options load
      if (availableOptions.length === 0) break;

      //  MAIN SUCCESS CASE
      if (matchedOption) {
        const chosenLabel =
          matchedOption.text || matchedOption.label || matchedOption.value;

        if (formField === "securityGroupID") {
          //  FIXED (critical)
          handleChange(formField, [matchedOption.value], chosenLabel);
        } else {
          handleChange(formField, matchedOption.value, chosenLabel);
        }

        updatedAField = true;
        break;
      }

      //  FALLBACK FOR SG (if not found)
      if (formField === "securityGroupID" && !matchedOption) {
        console.warn(" SG not found in current VPC → forcing value");

        handleChange(
          "securityGroupID",
          [incomingVal],
          incomingVal
        );

        updatedAField = true;
        break;
      }
    }

    if (updatedAField) return;

  }, [
    sidebarContext,
    dropdownData,
    formConfig,
    formData.location?.value,
    formData.vpcId?.value,
    formData.subnet?.value,
    formData.securityGroupID?.value
  ]);


  useEffect(() => {
    const fetchBudgetLimit = async () => {
      try {
        const response = await api.get('/Project/GetSetupDetailsById/1', {
          headers: { 'accept': 'text/plain' }
        });

        if (response.data?.setupKeyValue) {
          setBudgetLimit(Number.parseFloat(response.data.setupKeyValue));
        }
      } catch (error) {
        console.error("Error fetching budget limit:", error);
        setBudgetLimit(1000);
      }
    };

    fetchBudgetLimit();
  }, []);

  useEffect(() => {
    const subscriptionKey =
      selectedProvider?.toUpperCase() === "AWS"
        ? localStorage.getItem("selectedAccountId")
        : localStorage.getItem("selectedSubscription");
    const requestKey = JSON.stringify([
      selectedProvider,
      resourceId,
      resourceName,
      templateId,
      subscriptionKey,
      isTemplateFlow,
    ]);

    if (formConfigRequestKeyRef.current === requestKey) {
      return;
    }

    formConfigRequestKeyRef.current = requestKey;

    const fetchFormConfig = async () => {
      try {
        if (isTemplateFlow) {
          setLoading(true);
          updateTemplateLoading(
            "Preparing Template",
            "Loading the resource form before we apply your selected template.",
            15
          );
        }

        const resourceIdToUse = resourceId;

        const response = await api.get(
          `/api/Resources/form/${resourceIdToUse}/${subscriptionKey}`
        );
        if (isTemplateFlow) {
          updateTemplateLoading(
            "Analyzing Template",
            "Reviewing form metadata and matching template fields.",
            30
          );
        }
        const sortedConfig = response.data.sort(
          (a, b) => a.orderOfField - b.orderOfField
        );

        const copiedConfig = sortedConfig.map((field) => ({ ...field }));
        const updatedConfig = isAzureStorageAccount
          ? normalizeStorageAccountFormConfig(copiedConfig)
          : copiedConfig;



        setFormConfig(updatedConfig);

        if (!templateId) {
          setLoading(false)
        }
        const initialFormData = {};
        updatedConfig.forEach((field) => {
          let defaultValue = "";
          let defaultLabel = "";

          if (field.fieldType === "checkbox") {
            defaultValue = isCheckboxChecked(field.defaultValue);
            defaultLabel = defaultValue;
          }
          else if (field.fieldType === "multiSelectDropdown") {
            defaultValue = [];
          }
          else if (field.fieldName === "Email" || field.fieldName === "Owner") {
            defaultValue = sessionStorage.getItem("emailId") || "";
            defaultLabel = sessionStorage.getItem("emailId") || "";
          } 
          else if (field.fieldName === "Customer" || field.fieldName === "Company") {
            const orgName = sessionStorage.getItem('organizationName');
            defaultValue = orgName;
            defaultLabel = orgName;
          } else if (field.fieldName === "ManagedBy") {
            defaultValue = "NTT";
            defaultLabel = "NTT";
          } else if (field.fieldName === "CreatedBy") {
            defaultValue = "CMP";
            defaultLabel = "CMP";
          } else if (field.fieldName === "Account") {
            defaultValue = localStorage.getItem("selectedAccountId") || "";
            defaultLabel = localStorage.getItem("selectedAccountId") || "";
          } else if (field.fieldName === "availabilityType") {
            defaultValue = "Private";
            defaultLabel = "Private";
          } else if (field.fieldName === "priority") {
            defaultValue = "4";
            defaultLabel = "Low";
          } else if (field.fieldName === "Device_Type") {
            defaultValue = resourceName;
            defaultLabel = resourceName;
          } else {
            defaultValue = field.defaultValue ?? "";
            defaultLabel = field.defaultValue ?? "";
          }

          initialFormData[field.fieldName] = {
            value: defaultValue,
            label: defaultLabel,
          };
        });
        initialFormData["ActiveAccountID"] = {
          value: activeAccountId,
          label: activeAccountId
        };
        setFormData({
          ...initialFormData,
          ...(isAzure ? getAzureFormDefaults(updatedConfig) : {}),
        });
      } catch (error) {
        if (formConfigRequestKeyRef.current === requestKey) {
          formConfigRequestKeyRef.current = null;
        }
        console.error("Error fetching form configuration:", error);
        showToast(error.message || "Failed to load resource form.", "danger");
        setLoading(false);
      }
    };

    fetchFormConfig();
  }, [
    selectedProvider,
    resourceId,
    resourceName,
    templateId,
    activeAccountId,
    isTemplateFlow,
  ]);

  useEffect(() => {
    if (!formConfig.length) return;

    setFormData((previousFormData) => {
      if (
        previousFormData.ActiveAccountID?.value === activeAccountId &&
        previousFormData.ActiveAccountID?.label === activeAccountId
      ) {
        return previousFormData;
      }

      return {
        ...previousFormData,
        ActiveAccountID: {
          value: activeAccountId,
          label: activeAccountId,
        },
      };
    });
  }, [activeAccountId, formConfig.length]);


  useEffect(() => {
    if (!mode || !editResourceId) return;
    if (!formConfig.length) return;
    if (!dropdownData.location || dropdownData.location.length === 0) return;

    preloadForEditOrClone(mode, editResourceId, clonedResource);
  }, [mode, editResourceId, clonedResource, formConfig.length, dropdownData.location?.length]);



  useEffect(() => {
    const fetchNamingStandard = async () => {
      try {
        let resourceType = resourceName?.toLowerCase();
        if (resourceType === "security groups") {
          resourceType = "sgp";
        } else if (resourceType === "s3 bucket") {
          resourceType = "s3";
        } else if (resourceType.includes("rds")) {
          resourceType = "rds";
        }

        const response = await api.get('/api/AwsEc2/NamingStandards', {
          params: { resourceType },
          headers: {
            'X-CORRELATION-ID': 'd1364ce1-cb45-455e-ad07-f3b80868cd13',
            accept: 'text/plain',
          },
        });

        setNamingStandard(response.data);

       } catch (error) {
        console.error('Error fetching naming standard:', error);
      }
    };

    if (resourceName) {
      fetchNamingStandard();
    }
  }, [resourceName]);

  useEffect(() => {
    const fetchDiskPrice = async () => {
      const selectedMeter = formData["selectedMeter"]?.value;
      if (!selectedMeter) return;

      try {
        const response = await api.get(
          `/api/AzureResource/managed-disk-price/${selectedMeter}`
        );
        setDiskPrice(response.data);
      } catch (error) {
        console.error("Error fetching disk price:", error);
      }
    };

    fetchDiskPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData["selectedMeter"]?.value]);

  useEffect(() => {
    const cloudProviderId = localStorage.getItem("selectedProvider") === "AWS" ? 2 : 1;
    const resourceType = resourceId;
    const OrganizationId = sessionStorage.getItem("organizationId")

    if (resourceName?.toLowerCase().includes("rds")) return;

    if (resourceName?.toLowerCase() === "s3 bucket") {
      const location = formData["location"]?.value;
      if (!location) {
        setAwsPrice(null);
        return;
      }

      const fetchS3Price = async () => {
        try {
          const response = await api.get(
            `/api/AwsEc2/pricing/${OrganizationId}/${cloudProviderId}/null/null/${location}/${resourceType}`,
            {
              headers: {
                "X-CORRELATION-ID": "b104b1d3-8629-4adb-8f98-9200314a3e73",
                accept: "text/plain",
              },
            }
          );
          setAwsPrice(response.data);
        } catch (error) {
          console.error("Error fetching S3 price:", error);
          setAwsPrice(null);
        }
      };

      fetchS3Price();
    }
    else if (resourceName?.toLowerCase().includes("nlb") ||
      resourceName?.toLowerCase().includes("alb")
    ) {
      const location = formData["location"]?.value;
      if (!location) {
        setAwsPrice(null);
        return;
      }

      const fetchNlbPrice = async () => {
        try {
          const response = await api.get(
            `/api/AwsEc2/pricing/${OrganizationId}/${cloudProviderId}/null/null/${location}/${resourceType}`,
            {
              headers: {
                "X-CORRELATION-ID": "b104b1d3-8629-4adb-8f98-9200314a3e73",
                accept: "text/plain",
              },
            }
          );
          setAwsPrice(response.data);
        } catch (error) {
          console.error("Error fetching NLB price:", error);
          setAwsPrice(null);
        }
      };

      fetchNlbPrice();
    }


    else {
      const instanceType = formData["Ec2instance"]?.value || '';
      const platformDetails = formData["platformDetails"]?.value || '';
      const valueToInsert = platformDetails
        ? encodeURIComponent(platformDetails)
        : "";
      const location = formData["location"]?.value;

      if (!instanceType || !platformDetails || !location) {
        setAwsPrice(null);
        return;
      }

      const fetchAwsPrice = async () => {
        try {
          const response = await api.get(
            `/api/AwsEc2/pricing/${OrganizationId}/${cloudProviderId}/${instanceType}/${valueToInsert}/${location}/${resourceType}`,
            {
              headers: {
                "X-CORRELATION-ID": "b104b1d3-8629-4adb-8f98-9200314a3e73",
                accept: "text/plain",
              },
            }
          );
          setAwsPrice(prev => {
            if (JSON.stringify(prev) === JSON.stringify(response.data)) return prev;
            return response.data;
          });
        } catch (error) {
          console.error("Error fetching AWS price:", error);
        }
      };

      fetchAwsPrice();
    }
  }, [
    formData["Ec2instance"]?.value,
    formData["platformDetails"]?.value,
    formData["location"]?.value,
    formData["AwsAmis"]?.value,
    resourceName,
    selectedProvider,
    resourceId
  ]);

 useEffect(() => {
    if (!resourceName?.toLowerCase().includes("rds")) return;

    const organizationId = Number(sessionStorage.getItem("organizationId") || 1);
    const cloudProviderId = Number(localStorage.getItem("selectedProviderId") || 2);
    const location = formData["location"]?.value;
    const engine = formData["engine"]?.value;
    const dbInstanceType =
      formData["dbInstanceType"]?.value ||
      formData["postgresInstanceType"]?.value ||
      formData["sqlServerInstanceType"]?.value;
      
    const allocatedStorage = formData["allocatedStorage"]?.value || formData["AllocatedStorage"]?.value;

    if (!location || !engine || !dbInstanceType || !allocatedStorage) {
      setAwsPrice(null);
      return;
    }

    if (pricingTimeoutRef.current) {
      clearTimeout(pricingTimeoutRef.current);
    }

    pricingTimeoutRef.current = setTimeout(() => {
      const fetchRdsPrice = async () => {
        try {
          const payload = {
            organizationId: organizationId,
            cloudProviderId: cloudProviderId,
            resourceType: String(resourceId),
            location: location,
            engine: engine,
            sqlServerEdition: formData["sqlServerEdition"]?.value || "",
            multiAz: formData["multiAz"]?.value || "Single-AZ",
            dbInstanceType: dbInstanceType,
            storageType: formData["storageType"]?.value || "gp3",
            allocatedStorage: Number(allocatedStorage),
          };

          const response = await api.post("/api/AwsEc2/RDSPricing", payload, {
            headers: {
              "X-CORRELATION-ID": crypto.randomUUID(),
              accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          setAwsPrice(response.data);
        } catch (error) {
          console.error("Error fetching RDS pricing:", error);
          setAwsPrice(null);
        }
      };

      fetchRdsPrice();
    }, 1600);

    return () => {
      if (pricingTimeoutRef.current) {
        clearTimeout(pricingTimeoutRef.current);
      }
    };
  }, [
    formData["location"]?.value,
    formData["engine"]?.value,
    formData["sqlServerEdition"]?.value,
    formData["multiAz"]?.value,
    formData["dbInstanceType"]?.value,
    formData["postgresInstanceType"]?.value,
    formData["sqlServerInstanceType"]?.value,
    formData["storageType"]?.value,
    formData["allocatedStorage"]?.value,
    formData["AllocatedStorage"]?.value,
    resourceName,
    resourceId,
  ]);

  useEffect(() => {
    const fetchPrice = async () => {
      const selectedTemplateOption =
        formData["TemplateOptions"]?.value || formData["TemplateOptions"];
      if (!selectedTemplateOption) return;

      try {
        let priceResponse;
        if (selectedTemplateOption === 1) {
          priceResponse = await api.get(`/api/AzureResource/1-vmPrice`, {
            headers: {
              "X-CORRELATION-ID": "fe2ff096-646a-4619-99b2-157f066acd4d",
              accept: "text/plain",
            },
          });
        } else if (selectedTemplateOption === 2) {
          priceResponse = await api.get(
            `/api/AzureResource/golden-image-price/2/${resourceId}`,
            {
              headers: {
                "X-CORRELATION-ID": "fe2ff096-646a-4619-99b2-157f066acd4d",
                accept: "text/plain",
              },
            }
          );
        }

        setTemplatePrice(priceResponse.data);
        setVmPriceFetched(true);
      } catch (error) {
        console.error("Error fetching template price:", error);
      }
    };

    fetchPrice();
  }, [formData["TemplateOptions"]?.value, resourceId]);

  const fetchDropdownForField = async (fieldName, formData) => {
    await fetchDropdownData(fieldName, formData);
    return {
      options: dropdownData[fieldName] || [],
    };
  };

  const fetchDropdownData = async (fieldToUpdate, currentFormData) => {
    const subscriptionKey =
      selectedProvider?.toUpperCase() === "AWS"
        ? localStorage.getItem("selectedAccountId")
        : localStorage.getItem("selectedSubscription");

    const fetchDataForField = async (field) => {
      const formValues = currentFormData || formData;

      if (
        isAzureStorageAccount &&
        !areStorageDropdownParentsReady(field, formValues, {
          activeAccountId,
          subscriptionKey,
          userId,
          resourceType,
        })
      ) {
        return;
      }

      if (
        !canFetchDropdownData({
          field,
          formValues,
          activeAccountId,
          subscriptionKey,
          userId,
          rootDropdownsFetchedRef,
          formConfig,
        })
      ) {
        return;
      }

      if (!acquireDropdownLock(dropdownRequestRef, field.fieldName)) {
        return;
      }

      setDropdownLoading(prev => ({ ...prev, [field.fieldName]: true }));

      try {
        const url = buildDropdownUrl({
          field,
          formValues,
          activeAccountId,
          subscriptionKey,
          userId,
          resourceType,
          formConfig,
        });

        if (!url) return;

        // Skip only when this field already holds options loaded from the same URL.
        if (
          loadedDropdownUrlRef.current[field.fieldName] === url &&
          dropdownDataRef.current[field.fieldName]?.length > 0
        ) {
          return;
        }

        const response = await api.get(url);
        const apiData = extractDropdownApiData(response);

        const map = new Map();
        apiData.forEach(item => {
          const key = item[field.valueField] || item.value;
          const label = item[field.labelField] || item.label || item.text;
          const approved = item.isApproved === true;

          if (map.has(key)) {
            const existing = map.get(key);
            map.set(key, {
              ...existing,
              isApproved: existing.isApproved || approved,
            });
          } else {
            map.set(key, {
              value: key,
              label: label,
              isApproved: approved,
              osType: item.osType ?? null,
              vmSizeName: item.vmSizeName ?? null,
              code: item.code ?? null,
              placeholderValue: item.placeholderValue ?? "",
              validationPattern: item.validationPattern ?? "",
              restrictedRecordTypeId: item.restrictedRecordTypeId ?? null,
              isContinent: item.isContinent ?? null,
            });
          }
        });

        const processedData = Array.from(map.values());

        loadedDropdownUrlRef.current[field.fieldName] = url;
        dropdownDataRef.current = {
          ...dropdownDataRef.current,
          [field.fieldName]: processedData,
        };
        setDropdownData(prev => ({
          ...prev,
          [field.fieldName]: processedData,
        }));
      } catch (error) {
        console.error(`Error fetching dropdown data for ${field.fieldName}:`, error);
      } finally {
        releaseDropdownLock(dropdownRequestRef, field.fieldName);
        setDropdownLoading(prev => ({ ...prev, [field.fieldName]: false }));
      }
    };

    const fieldsToProcess = fieldToUpdate
      ? formConfig.filter(f => f.fieldName === fieldToUpdate)
      : formConfig;

    for (const field of fieldsToProcess) {
      if (
        field.dropdownType === "api" &&
        field.fieldType !== "text" &&
        field.fieldName !== "Name"
      ) {
        await fetchDataForField(field);
      } else if (field.dropdownType === "table" || field.dropdownType === "SP") {
        try {
          const options = JSON.parse(field.dropdownValues);
          setDropdownData(prev => ({ ...prev, [field.fieldName]: options }));
        } catch {
          setDropdownData(prev => ({ ...prev, [field.fieldName]: [] }));
        }
      }
    }
  };

  useEffect(() => {
    if (!formConfig.length) return;
    if (rootDropdownsFetchedRef.current) return;

    const rootFields = formConfig.filter(
      f => f.dropdownType === "api" && !f.parentField
    );

    rootFields.forEach(f => {
      fetchDropdownData(f.fieldName, formData);
    });

    rootDropdownsFetchedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formConfig]);

  const trimLeadingSpaces = (value) => {
    if (typeof value !== "string") return value;
    return value.replace(/^\s+/, "");
  };

  const handleChange = (fieldName, value, label = "") => {
    if (isAzureVirtualMachine) {
      setTouchedVmFields((previous) => ({ ...previous, [fieldName]: true }));
    }
    let updatedValue = value;
    let updatedLabel = label;



    const preservePassword = isAzureVirtualMachine &&
      formConfig.some((field) => field.fieldName === fieldName && field.fieldType === "password");
    if (typeof value === "string" && !preservePassword) {
      updatedValue = trimLeadingSpaces(value);
    }

    if (typeof label === "string") {
      updatedLabel = trimLeadingSpaces(label);
    }



     const isS3 = resourceName?.toLowerCase().includes("s3");

    // Auto-convert Application ID to lowercase for S3
    if (fieldName === "applicationId") {
      updatedValue = String(value || "").replace(/\s+/g, "");
      if (isS3) updatedValue = updatedValue.toLowerCase();
      updatedLabel = updatedValue;
    }

    // Auto-convert Application Name to lowercase for S3
    if (isS3 && (fieldName === "ApplicationName" || fieldName === "applicationName")) {
      if (typeof updatedValue === "string") updatedValue = updatedValue.toLowerCase();
      if (typeof updatedLabel === "string") updatedLabel = updatedLabel.toLowerCase();
    }


    const { value: normalizedValue, label: normalizedLabel } = normalizeValueLabel(updatedValue, updatedLabel);


    // const handleChange = (fieldName, value, label = "") => {
    //   const { value: normalizedValue, label: normalizedLabel } = normalizeValueLabel(value, label);

    if (apiValidationFields.includes(fieldName)) {
      setApiValidationFields(prev => prev.filter(f => f !== fieldName));
    }

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [fieldName]: {
          value: normalizedValue,

          
          label: normalizedLabel,
        },
      };
      const dependentFields = getDependentFields(formConfig, fieldName);

      if (fieldName === "resourceName" || fieldName === "Name" || fieldName === "dbIdentifier") {
        setUseSuffixMode(false);
        
        newData.resourceName = { value: normalizedValue, label: normalizedLabel };
        newData.Name = { value: normalizedValue, label: normalizedLabel };
        newData.dbIdentifier = { value: normalizedValue, label: normalizedLabel };
      }

      if (fieldName === "platformDetails") {
        [
          "Operating_system",
          "Specific_Type",
          "AwsAmis",
          "IvantiMachineGroup",
          "IvantiCredential",
          "IvantiDeploymentTemplate"
        ].forEach((depField) => {
          newData[depField] = {
            value: depField === "IvantiDeploymentTemplate" ? [] : "",
            label: "",
          };
        });

        setDropdownData((prev) => ({
          ...prev,
          Operating_system: [],
          Specific_Type: [],
          AwsAmis: [],
          IvantiMachineGroup: [],
          IvantiCredential: [],
          IvantiDeploymentTemplate: [],
        }));

        Object.keys(dropdownDependencyCache.current).forEach((key) => {
          if (
            key.startsWith("IvantiMachineGroup::") ||
            key.startsWith("IvantiCredential::") ||
            key.startsWith("IvantiDeploymentTemplate::")
          ) {
            delete dropdownDependencyCache.current[key];
          }
        });
      }

      Object.keys(dropdownDependencyCache.current).forEach((key) => {
        if (dependentFields.some((f) => key.startsWith(`${f.fieldName}::`))) {
          delete dropdownDependencyCache.current[key];
        }
      });

      if (fieldName === "environment") {
        newData.Environment = { value: normalizedValue, label: normalizedLabel || normalizedValue };
      }
      if (fieldName === "Environment") {
        newData.environment = { value: normalizedValue, label: normalizedLabel || normalizedValue };
      }

      // Storage visibility is derived from all ancestors without clearing checkbox defaults or selections.
      dependentFields.filter(field =>
        !(isAzureStorageAccount && isStorageDataProtectionField(field))
      ).forEach(field =>
        processDependentField({
          field,
          triggeringField: fieldName,
          normalizedValue,
          newData: newData, // Pass the freshly updated object
          dropdownDependencyCache,
          setFormConfig,
          setDropdownData,
          fetchDropdownData,
        })
      );

      previousFormDataRef.current = newData;
      return newData;
    });
  };


  const constructResourceName = (formData, template) => {
    if (!template || typeof template !== "string") {
      return "";
    }

    const appId = (formData["applicationId"]?.value || "").replace(/\s+/g, "");
    const appName = formData["ApplicationName"]?.value || "";
    const envLabel = formData["environment"]?.label || "";

    const rawRegion = formData["location"]?.value;

    const regionValue =
      typeof rawRegion === "object"
        ? rawRegion.value ?? rawRegion.label ?? rawRegion.text ?? ""
        : rawRegion ?? "";

    if (!appId && !appName && !envLabel && !regionValue) {
      return "";
    }

    const os =
      typeof formData["platformDetails"]?.value === "string"
        ? formData["platformDetails"].value.charAt(0)
        : "";

    const env =
      dropdownData["environment"]?.find(
        (option) => option.label === envLabel
      )?.code || "";

    const regionOption = dropdownData["location"]?.find((option) => option.value === regionValue && option.isApproved); const region = regionOption ? regionOption.code : "";


    const tenant = "cm";
    const platform = "pfe-pg";

    let resourceType = resourceName?.toLowerCase();
    if (resourceType === "security groups") resourceType = "sgp";
    if (resourceType === "s3 bucket") resourceType = "s3";

    const productAbr = appName
      .replaceAll(/\s+/g, "")
      .substring(0, 4)
      // .toLowerCase();

    const computeType =
      typeof location.state?.resourceName === "string"
        ? location.state.resourceName.toLowerCase()
        : "";

    const id =
      typeof formData["applicationId"]?.value === "string"
        ? formData["applicationId"].value.replace(/\s+/g, "").substring(0, 2)
        : "";


    const vpcAbr = "vpc";

    const name = template
      .replace("{os}", os.toLowerCase())
      .replace("{env}", env)
      .replace("{region}", region)
      .replace("{tenant}", tenant)
      .replace("[tenant]", tenant)
      .replace("{resource-type}", resourceType)
      .replace("{product-abr}", productAbr)
      .replace("{vpc-abr}", vpcAbr)
      .replace("{compute-type}", computeType)
      .replace("{id}", id)
      .replace("id", id)
      .replace("{platform-name}", platform)
      .replace("|", "");


    return resourceName?.toLowerCase().includes("s3") ? name.toLowerCase() : name;

    // return name;
   };

  useEffect(() => {
    if (!namingStandard || isManualResourceName) return;

    const updated = constructResourceName(formData, namingStandard);
    if (!updated) return;

    setFormData(prev => {
      const currentName = prev.resourceName?.value || "";

      if (currentName === updated) {
        return prev;
      }

      if (currentName !== "" && currentName.startsWith(updated)) {
        return prev;
      }

      return {
        ...prev,
        resourceName: { value: updated, label: updated },
        Name: { value: updated, label: updated },
        dbIdentifier: { value: updated, label: updated },
      };
    });
  }, [
    namingStandard,
    isManualResourceName,
    formData.applicationId?.value,
    formData.ApplicationName?.value,
    formData.environment?.label,
    formData.location?.value,
    formData.platformDetails?.value,
    dropdownData.environment
  ]);

  const addTag = () => {
    const stats = calculateTagStats();

    if (stats.totalUsed >= MAX_TOTAL_TAGS) {
      toast.error(` Cannot add more tags`);
      return;
    }

    if (stats.remaining <= 0) {
      toast.error(` Can’t add more tags`);
      return;
    }
    setAdditionalTags([...additionalTags, { id: crypto.randomUUID(), key: "", value: "" }]);
  };

  const removeTag = (index) => {
    setAdditionalTags((prevTags) => prevTags.filter((_, i) => i !== index));
  };



  const handleTagChange = (index, field, value) => {

    const sanitizedValue = trimLeadingSpaces(value);

    setAdditionalTags((prevTags) => {
      const newTags = [...prevTags];
      newTags[index][field] = sanitizedValue;
      return newTags;
    });
  };

  const renderAdditionalTags = () => (
    <Box sx={{ mt: 1 }}>
      {additionalTags.map((tag, index) => {
        const safeValue = renderSafeText(tag.value);

        return (
          <Box
            key={tag.id || index}
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid",
              borderColor: (t) => t.palette.mode === "dark" ? "divider" : "#eee",
              borderRadius: "4px",
              overflow: "hidden",
              mb: 2,
              backgroundColor: "transparent",
              transition: "border-color 0.2s ease",
              "&:hover": {
                borderColor: "#1976d2"
              },
              "&:focus-within": {
                borderColor: "#1976d2",
                borderWidth: "2px",
                padding: "0px"
              }
            }}
          >
            <TextField
              placeholder="Tag Name"
              disabled={disabledFields["Tag"] === true}
              variant="standard"
              value={renderSafeText(tag.key)}
              onChange={(e) => handleTagChange(index, "key", e.target.value)}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                flex: 1,
                px: 2,
                py: 1.8,
                borderRight: "1px solid",
                borderColor: (t) => t.palette.mode === "dark" ? "divider" : "rgba(0, 0, 0, 0.23)",

                "& .MuiInputBase-root": {
                  fontSize: "14px",
                },

                "& input": {
                  padding: 0,
                }
              }}
            />

            <TextField
              placeholder="Tag Value"
              disabled={disabledFields["Tag"] === true}
              variant="standard"
              value={safeValue}
              onChange={(e) => handleTagChange(index, "value", e.target.value)}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                flex: 1,
                px: 2,
                py: 1.8,
                borderRight: "1px solid",
                borderColor: (t) => t.palette.mode === "dark" ? "divider" : "rgba(0, 0, 0, 0.23)",

                "& .MuiInputBase-root": {
                  fontSize: "14px",
                },

                "& input": {
                  padding: 0,
                }
              }}
            />

            <IconButton
              color="error"
              onClick={() => removeTag(index)}
              sx={{
                width: 60,
                height: 56,
                borderRadius: 0,
              }}
            >
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );

  const shouldHideField = (field, currentFormData) => {
    if (field.subTab === "Agent Installation" && !resourceName?.toLowerCase().includes("ec2")) {
      return true;
    }    

    if (isAzureStorageAccount && isStorageDataProtectionField(field)) {
      return !isStorageFieldVisible(field, formConfig, currentFormData);
    }

    if (!field.isDisplay) return true;

    if (field.dependentOn && field.dependentValue) {
      const rawParentValue = currentFormData?.[field.dependentOn];

      const parentValue = rawParentValue?.value !== undefined ? rawParentValue.value : rawParentValue;

      const expectedValues = String(field.dependentValue).split(',');

      if (!expectedValues.includes(String(parentValue))) {
        return true;
      }
    }

    const hiddenFieldNames = [
      "Name",
      "CreatedBy",
      "Account",
      "priority",
    ];

    if (hiddenFieldNames.includes(field.fieldName)) {
      return true;
    }

    const isLogicMonitor =
      field.tabName === "Tag" && field.subTab === "Logic Monitor";

    if (
      isLogicMonitor &&
      (field.fieldName === "Customer" || field.fieldName === "Environment")
    ) {
      return true;
    }

    return false;
  };


  const isAzureResourceGroupNameField = (field) => {
  if (selectedProvider?.toUpperCase() !== "AZURE") return false;

  const values = [field?.fieldName, field?.fieldLabel].map((value) =>
    String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "")
  );

  return values.some(
    (value) => value === "resourcegroup" || value === "resourcegroupname"
  );
};


  const renderTextField = (field, isError, vmError = "") => {

    const isRdsCredentialField =
      !isAzureVirtualMachine && (field.subTab === "Credentials" ||
      ["masterUsername", "masterPassword", "confirmMasterPassword"].includes(field.fieldName));

    const rawValue = formData[field.fieldName]?.value;

   const isAzureResourceGroupField =
  isAzureResourceGroupNameField(field);


const showResourceGroupError =
  isAzureResourceGroupField && !isAzureVirtualMachine &&
  (String(rawValue ?? "").trim() !== "" ||
    (field.isRequired && (isError || formSubmitted || validatedTab === activeTab)));

const resourceGroupNameError = showResourceGroupError
  ? getResourceGroupNameError(rawValue)
  : "";

const isStorageNameField =
  isAzureStorageAccount &&
  isAzureStorageNameField(field.fieldName);

const showStorageNameError =
  isStorageNameField &&
  (String(rawValue ?? "").trim() !== "" ||
    (field.isRequired && (isError || formSubmitted || validatedTab === activeTab)));

const storageNameError = showStorageNameError
  ? getAzureStorageAccountNameError(rawValue)
  : "";

const isStorageIpv4Field =
  isAzureStorageAccount &&
  isAzureIpv4Field(field.fieldName);

const ipv4Error = isStorageIpv4Field
  ? getAzureIpv4OrCidrError(rawValue)
  : "";


    const isNameField = field.fieldName === "resourceName" || field.fieldName === "Name";
    const hasApiError = apiValidationFields.includes(field.fieldName);

    let finalIsError =
      isError ||
      (forceNameError && isNameField) ||
      hasApiError ||
      Boolean(resourceGroupNameError) ||
      Boolean(storageNameError) ||
      Boolean(ipv4Error);

    const isPassword = field.fieldType === "password";
    const InputComponent = isPassword ? PasswordInputField : TextField;

    const isStorageField = !isAzureVirtualMachine &&
      (field.fieldLabel?.includes("Allocated Storage") || field.fieldName === "AllocatedStorage");
    const minStorage = 20;
    const maxStorage = 65536;

    let storageErrorMsg = "";

    if (isStorageField && rawValue !== "" && rawValue !== undefined) {
      const val = Number(rawValue);
      if (val < minStorage) {
        finalIsError = true;
        storageErrorMsg = `Minimum storage allowed is ${minStorage} GiB`;
      } else if (val > maxStorage) {
        finalIsError = true;
        storageErrorMsg = `Maximum storage allowed is ${maxStorage.toLocaleString()} GiB`;
      }
    }

    let passwordErrorMsg = "";
    if (!isAzureVirtualMachine && field.fieldName === "masterPassword" && rawValue) {
      const pwdError = getPasswordError(rawValue);
      if (pwdError) {
        finalIsError = true;
        passwordErrorMsg = pwdError;
      }
    }

    let usernameErrorMsg = "";
    if (!isAzureVirtualMachine && field.fieldName === "masterUsername" && rawValue) {
      const uError = getUsernameError(rawValue);
      if (uError) {
        finalIsError = true;
        usernameErrorMsg = uError;
      }
    }

    let helperTextMsg = "";
    
    if (finalIsError) {
      if (resourceGroupNameError) {
  helperTextMsg = resourceGroupNameError;
} else if (storageNameError) {
        helperTextMsg = storageNameError;
      } else if (ipv4Error) {
        helperTextMsg = ipv4Error;
} else if (storageErrorMsg) {
        helperTextMsg = storageErrorMsg;
      } else if (passwordErrorMsg) {
        helperTextMsg = passwordErrorMsg; 
      } else if (usernameErrorMsg) {         
        helperTextMsg = usernameErrorMsg;   
      } else {
        helperTextMsg = forceNameError && isNameField
          ? "Resource name already exists"
          : (hasApiError ? apiFieldErrorText : "This field is required");
      }
    } else {
      if (isStorageField) {
        helperTextMsg = `Minimum: ${minStorage} GiB. Maximum: ${maxStorage.toLocaleString()} GiB`;
      } else if (field.fieldName === "masterPassword") {
        helperTextMsg = "Minimum constraints: At least 8 printable ASCII characters. Can't contain any of the following symbols: / ' \" @";
      }
    }

    if (isStorageField && !finalIsError) {
      helperTextMsg = `Minimum: ${minStorage} GiB. Maximum: ${maxStorage.toLocaleString()} GiB`;
    }
    if (vmError) {
      finalIsError = true;
      helperTextMsg = vmError;
    }

    return (
      <Box
        key={`${field.fieldId}-${field.fieldName}`}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, width: "100%" }}
      >
        <InputComponent
          name={field.fieldName}
          label={isStorageField ? "Allocated Storage" : field.fieldLabel}
          required={field.isRequired}
          value={renderSafeText(rawValue)}
          type={isPassword ? undefined : (isStorageField ? "number" : "text")}
          multiline={isAzureVirtualMachine && field.fieldType === "textarea"}

          autoComplete={
            isRdsCredentialField
              ? (isPassword ? "new-password" : "off")
              : undefined
          }
          inputProps={{
            autoComplete: isRdsCredentialField
              ? (isPassword ? "new-password" : "off")
              : undefined,
            min: isStorageField ? minStorage : undefined,
            max: isStorageField ? maxStorage : undefined,
            maxLength: isAzureResourceGroupField
              ? 90
              : isStorageNameField
              ? 24
              : undefined,
          }}

          InputProps={isStorageField ? {
            endAdornment: <InputAdornment position="end">GiB</InputAdornment>
          } : undefined}
          
          onKeyDown={(e) => {
            if (isStorageField && (e.key === "-" || e.key === "e" || e.key === "+" || e.key === ".")) {
              e.preventDefault();
            }
          }}

          onChange={(e) => {
            let val = e.target.value;

            if (isStorageField && val !== "") {
              val = val.replace(/^0+(?=\d)/, '');
              
              if (Number(val) > maxStorage) {
                return; 
              }
            }

            if (isStorageNameField) {
              val = String(val ?? "")
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "")
                .slice(0, 24);
            }

            handleChange(field.fieldName, val);
            
            if (forceNameError && isNameField) {
              setForceNameError(false);
            }
          }}
          placeholder={field.placeholder || ""}
          margin="normal"
          error={finalIsError}
          helperText={helperTextMsg}
          disabled={
            field.isEnabled === false ||
            disabledFields[field.fieldName] === true ||
            [
              "Name",
              "Email",
              "Customer",
              "Company",
              "ManagedBy",
              "Account",
              "Device_Type",
              "dbIdentifier",
              "Owner"
            ].includes(field.fieldName)
          }
          sx={{ width: "100%", m: 0 }}
        />
      </Box>
    );
  };

  const renderDropdownField = (field, isError, vmError = "") => {
    const rawOptions = dropdownData[field.fieldName] || [];
    const hasApiError = apiValidationFields.includes(field.fieldName);
    const finalIsError = isError || hasApiError || Boolean(vmError);

    const options = [...rawOptions].sort((a, b) => {
      if (a.isApproved === b.isApproved) return 0;
      return a.isApproved ? -1 : 1;
    });

    const currentVal = formData[field.fieldName]?.value;

    const getGroupLabel = (option) => {
      if (field.fieldName === "location") {
        return option.isApproved ? "Approved" : "Non-Approved";
      }
      return "";
    };

    return (
      <FormControl sx={{ mt: 1, width: "100%" }}>
        <Autocomplete
          disabled={disabledFields[field.fieldName] === true}
          options={options}
          groupBy={getGroupLabel}
          value={
            options.find(o => String(o.value) === String(currentVal)) || null
          }
          getOptionLabel={getOptionLabelGeneric}
          isOptionEqualToValue={(opt, val) => String(opt.value) === String(val?.value)}
          renderGroup={(params) => (
            <Box component="li" key={params.key}>
              {params.group && (
                <Box sx={{ fontWeight: 'bold', px: 2, py: 1, bgcolor: "background.default", color: "text.primary" }}>
                  {params.group}
                </Box>
              )}
              <Box component="ul" sx={{ p: 0, '& > li': { listStyle: 'none' } }}>
                {params.children}
              </Box>
            </Box>
          )}
          onChange={(e, newValue) => {
            handleChange(
              field.fieldName,
              newValue?.value ?? "",
              newValue?.label ?? newValue?.text ?? ""
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name={field.fieldName}
              label={field.fieldLabel}
              required={field.isRequired}
              // error={isError} 
              // helperText={isError ? "This field is required" : ""}
              error={finalIsError}
              helperText={
                finalIsError
                  ? vmError || (hasApiError
                    ? apiFieldErrorText
                    : "This field is required")
                  : ""
              }
            />
          )}
        />
      </FormControl>
    );
  };

  const renderMultiSelectField = (field, vmError = "") => {
    const hasApiError = apiValidationFields.includes(field.fieldName);
    const options = dropdownData[field.fieldName] || [];
    const isSecurityGroup = field.fieldName === "securityGroupID" || field.fieldName === "securityGroupId";

    const rawValue = formData[field.fieldName]?.value;
    const selectedIds = Array.isArray(rawValue)
      ? rawValue.map(String)
      : rawValue ? [String(rawValue)] : [];
    const selectedOptions = selectedIds.map(id => {
      const found = options.find(o => String(o.value) === id);
      if (found) return found;
      return { value: id, label: id }; // Keeps them as separate Chips
    });

    const isAlbOrNlb =
      resourceName?.toUpperCase() === "ALB" ||
      resourceName?.toUpperCase() === "NLB";

    const isSubnetField = field.fieldName === "subnet";
    const enforceMinTwoSubnets = isAlbOrNlb && isSubnetField;

    const minCount = enforceMinTwoSubnets ? 2 : (field.isRequired ? 1 : 0);
    const maxCount = isSubnetField ? Infinity : 5;
    const hasValidationError =
      (formSubmitted || validatedTab === activeTab) &&
      (
        (field.isRequired && selectedIds.length === 0) ||
        (enforceMinTwoSubnets &&
          selectedIds.length > 0 &&
          selectedIds.length < minCount)
      );

    const hasMaxError = !!maxErrors[field.fieldName];


    return (
      <FormControl
        sx={{ mt: 1, width: "100%" }}
        error={hasValidationError || hasMaxError || hasApiError || Boolean(vmError)}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={disabledFields[field.fieldName] === true}
          options={options}
          value={selectedOptions}
          slotProps={{
            paper: {
              sx: {
                border: isSecurityGroup ? 1 : 0,
                borderColor: "divider",
                boxShadow: isSecurityGroup
                  ? "0px 8px 24px rgba(0, 0, 0, 0.12)"
                  : "0px 4px 10px rgba(0, 0, 0, 0.08)",
              }
            }
          }}
          getOptionLabel={getOptionLabelGeneric}
          isOptionEqualToValue={(opt, val) =>
            String(opt.value) === String(val?.value)
          }
          onChange={(e, newValue = []) => {
            if (newValue.length > maxCount) {
              setMaxErrors(prev => ({ ...prev, [field.fieldName]: true }));
              return;
            } else {
              setMaxErrors(prev => ({ ...prev, [field.fieldName]: false }));
            }

            const selectedValues = newValue.map(item => item.value);
            const selectedLabels = newValue.map(
              item => item.label ?? item.text ?? item.value
            );
            handleChange(
              field.fieldName,
              selectedValues,
              selectedLabels.join(", ")
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox
                  icon={
                    <span style={{
                      width: 18, height: 18, border: `2px solid ${theme.palette.text.secondary}`,
                      borderRadius: 3, display: 'inline-block'
                    }} />
                  }
                  checkedIcon={
                    <span style={{
                      width: 18, height: 18, backgroundColor: theme.palette.primary.main,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: 3, display: 'inline-block', position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        color: theme.palette.primary.contrastText, fontSize: '12px', fontWeight: 'bold'
                      }}>✓</span>
                    </span>
                  }
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <ListItemText
                  primary={option.label ?? option.text ?? option.value}
                />
              </li>
            );
          }}
          renderTags={(value, getTagProps) => {
            const chipSx = {
              borderColor: "divider",
              color: "text.primary",
              backgroundColor: (t) => t.palette.mode === 'dark' ? 'background.default' : '#f8f9fa',
              fontWeight: 500
            };

            if (value.length > 1) {
              return [
                <Chip
                  label={value[0].label ?? value[0].text ?? value[0].value}
                  {...getTagProps({ index: 0 })}
                  key={value[0].value}
                  color="primary"
                  variant="outlined"
                  sx={chipSx}
                />,
                <Chip
                  label={`+${value.length - 1} more`}
                  key="more"
                  color="primary"
                  variant="outlined"
                  sx={chipSx}
                />
              ];
            }

            return value.map((option, index) => (
              <Chip
                label={option.label ?? option.text ?? option.value}
                {...getTagProps({ index })}
                key={option.value}
                color="primary"
                variant="outlined"
                sx={chipSx}
              />
            ));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              name={field.fieldName}
              label={field.fieldLabel}
              required={field.isRequired}
              error={hasValidationError || hasMaxError || hasApiError || Boolean(vmError)}
              // helperText={
              // hasValidationError
              //     ? "This field is required"
              //     : hasMaxError
              //       ? `Maximum ${maxCount} options allowed`
              //       : hasApiError
              //         ? apiFieldErrorText
              //         : ""
              // }
              helperText={
                vmError || (hasValidationError
                  ? enforceMinTwoSubnets &&
                    selectedIds.length > 0 &&
                    selectedIds.length < minCount
                    ? "Minimum 2 subnets allowed"
                    : "This field is required"
                  : hasMaxError
                    ? `Maximum ${maxCount} options allowed`
                    : hasApiError
                      ? apiFieldErrorText
                      : "")
              }
              placeholder={
                selectedIds.length === 0
                  ? `Select ${field.fieldLabel}...`
                  : ""
              }
            />
          )}
        />

        {selectedOptions.length > 0 && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "text.secondary", wordBreak: 'break-all' }}
          >
            <strong>Selected: </strong>
            {selectedOptions
              .map(o => o.label ?? o.text ?? o.value)
              .join(', ')
            }
          </Typography>
        )}
      </FormControl>
    );
  };


  const renderRadioField = (field, isError, vmError = "") => {
    const hasApiError = apiValidationFields.includes(field.fieldName);
    const finalIsError = isError || hasApiError || Boolean(vmError);
    const isAvailability = field.fieldName === "availabilityType";
    const value = formData[field.fieldName]?.value || (isAvailability ? "Private" : "");
    const options = field.dropdownSource
      ? field.dropdownSource.split(",").map(opt => {
        const trimmed = opt.trim();
        return {
          value: trimmed,
          label: trimmed,
          isEnable: isAvailability && trimmed === "Public" ? false : true
        };
      })
      : [];

    return (
      <Box key={field.fieldId} sx={{ mt: 0 }}>
        <FormRadio
          name={field.fieldName}
          label={field.fieldLabel}
          disabled={disabledFields[field.fieldName] === true}
          options={options}
          value={value}
          required={field.isRequired}
          error={finalIsError}
          helperText={finalIsError ? (vmError || (hasApiError ? apiFieldErrorText : "This field is required")) : ""}
          onChange={(e) => {
            const selectedValue = e?.target ? e.target.value : e;

            handleChange(field.fieldName, {
              value: selectedValue,
              label: selectedValue
            });
          }}
        />
      </Box>
    );
  };

  const renderField = (field,) => {
    if (shouldHideField(field, formData)) return null;
    const vmError = isAzureVirtualMachine &&
      (touchedVmFields[field.fieldName] || formSubmitted || validatedTab === activeTab ||
        String(formData[field.fieldName]?.value ?? "").trim() !== "")
      ? getAzureVmFieldError(field, formData, formConfig)
      : "";

    const isError =
      (
        formSubmitted || validatedTab === activeTab
      ) &&
      field.isRequired &&
      !formData[field.fieldName]?.value;

    switch (field.fieldType) {
      case "text":
      case "password":
        return renderTextField(field, isError, vmError);
      case "number":
      case "textarea":
        return isAzureVirtualMachine ? renderTextField(field, isError, vmError) : null;
      case "dropdown":
        return renderDropdownField(field, isError, vmError);
      case "multiSelectDropdown":
        return renderMultiSelectField(field, vmError);
      case "Radio button":
        return renderRadioField(field, isError, vmError);
      case "checkbox": {
        const label = field.fieldLabel || field.fieldName;
        const storageCheckbox = isAzureStorageAccount
          ? getStorageCheckboxState(field, formConfig, formData)
          : null;
        const checked = storageCheckbox?.checked ?? isCheckboxChecked(
          formData[field.fieldName]?.value ?? field.defaultValue ?? false
        );
        const hasApiError = apiValidationFields.includes(field.fieldName);
        const checkboxError =
          hasApiError ||
          ((formSubmitted || validatedTab === activeTab) &&
            (storageCheckbox ? Boolean(storageCheckbox.message) : field.isRequired && !checked));

        return (
          <FormCheckbox
            name={field.fieldName}
            label={label}
            value={checked}
            onChange={(event) =>
              handleChange(field.fieldName, event.target.checked, field.fieldLabel)
            }
            required={field.isRequired}
            disabled={field.isEnabled === false || disabledFields[field.fieldName] === true || storageCheckbox?.disabled}
            error={checkboxError || Boolean(vmError)}
            helperText={vmError || (checkboxError ? (hasApiError ? apiFieldErrorText : storageCheckbox?.message || `${label} is required`) : "")}
          />
        );
      }
      case "collection":
        return null;
      default:
        return null;
    }
  };
  const renderTagsSection = () => {
    const isRuleOnlyEditMode = Object.values(disabledFields).some(
      v => v === true
    );
    return (
      <Box sx={{ mt: 1, ms: 4, width: "100%", display: "flex", flexDirection: "column", flexWrap: "wrap", }}>
        {renderAdditionalTags()}
        {activeTab === tabNames.indexOf("Tag") && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {resourceName.toLowerCase() !== "alb" &&
              resourceName.toLowerCase() !== "nlb" &&
              !resourceName.toLowerCase().includes("rds") &&
              !isRuleOnlyEditMode && (<Button
                variant="outlined"
                color="primary"
                onClick={addTag}
                sx={{ minWidth: "20px", mb: 3, mr: 2 }}
              >
                <AddOutlined fontSize="small" /> <Typography sx={{ paddingTop: '3px', fontSize: '10px', fontWeight: 'Bold' }}>Add Tag</Typography>
              </Button>)}
          </Box>
        )}
      </Box>)
  };

  const tabNames = [...new Set(formConfig.map((field) => field.tabName))];
  const subTabNames = formConfig.reduce((acc, field) => {
    if (field.subTab && field.tabName === tabNames[activeTab]) {
      acc.add(field.subTab);
    }
    return acc;
  }, new Set());

  const validateCollectionField = (collectionField, collectionValue) => {
    const errors = [];

    const rows = Array.isArray(collectionValue) ? collectionValue : [];

    if (collectionField.isRequired && rows.length === 0) {
      errors.push(collectionField.fieldLabel);
      return errors;
    }

    rows.forEach((row, rowIndex) => {
      (collectionField.collectionFields || []).forEach((childField) => {
        if (childField.fieldType === "collection") {
          const nestedRows = row?.[childField.fieldName]?.value || [];
          const nestedErrors = validateCollectionField(childField, nestedRows);

          nestedErrors.forEach((err) => {
            errors.push(`${collectionField.fieldLabel} ${rowIndex + 1} - ${err}`);
          });
        } else {
          const rawValue = row?.[childField.fieldName]?.value;
          const isEmpty = Array.isArray(rawValue)
            ? rawValue.length === 0
            : !rawValue;

          if (childField.isRequired && isEmpty) {
            errors.push(
              `${collectionField.fieldLabel} ${rowIndex + 1} - ${childField.fieldLabel}`
            );
          }
        }
      });
    });

    return errors;
  };

  const validateRequiredFields = (fields, data, parentLabel = "", globalData = null) => {
    const errors = [];
    const rootData = globalData || data; // Keep context of the root form for global dependencies

    fields.forEach((field) => {
      if (field.subTab === "Agent Installation" && !resourceName?.toLowerCase().includes("ec2")) {
        return;
      }

      const secureFields = [
        "SecurityCategory",
        "PolicyName",
        "CertificateSource",
        "CertificateFromACM"
      ];

      if (secureFields.includes(field.fieldName)) {

        const listeners = globalData?.Listeners?.value || [];

        const hasTLS = listeners.some(l => {
          const proto = (l?.Protocol?.value || "").toUpperCase();
          return proto === "TLS" || proto === "HTTPS";
        });

        //  SKIP validation when protocol is TCP
        if (!hasTLS) {
          return;
        }
      }


      if (globalData === null && field.isDisplay === false) {
        return;
      }

      if (!isDependencyMet(field, data, rootData)) {
        return;
      }

      const fieldPathLabel = parentLabel
        ? `${parentLabel} - ${field.fieldLabel}`
        : field.fieldLabel;

      const fieldValue = isAzureStorageAccount && isStorageDataProtectionField(field)
        ? getStorageFieldValue(field, data)
        : data?.[field.fieldName]?.value;

      const isAzureResourceGroupField =
  selectedProvider?.toUpperCase() === "AZURE" &&
  field.fieldName === "resourceGroup";

if (isAzureResourceGroupField) {
  const nameError = getResourceGroupNameError(fieldValue);
  const hasValue = String(fieldValue ?? "").trim() !== "";

  if (nameError && (field.isRequired || hasValue)) {
    errors.push(fieldPathLabel);
    return;
  }
}


      if (field.fieldType === "collection") {
        const rows = Array.isArray(fieldValue) ? fieldValue : [];

        if (field.isRequired && rows.length === 0) {
          errors.push(fieldPathLabel);
          return;
        }

        rows.forEach((row, index) => {
          const rowLabel = `${fieldPathLabel} ${index + 1}`;
          const nestedErrors = validateRequiredFields(
            field.collectionFields || [],
            row,
            rowLabel,
            rootData // Pass root data down so nested fields can check global dependencies
          );
          errors.push(...nestedErrors);
        });

        return;
      }

      const isEmpty = field.fieldType === "checkbox"
        ? !isCheckboxChecked(fieldValue)
        : Array.isArray(fieldValue)
        ? fieldValue.length === 0
        : fieldValue === undefined ||
        fieldValue === null ||
        String(fieldValue).trim() === "";

      const isAlbOrNlb =
        resourceName?.toUpperCase() === "ALB" ||
        resourceName?.toUpperCase() === "NLB";

      if (isAlbOrNlb && field.fieldName === "subnet") {
        if (!Array.isArray(fieldValue) || fieldValue.length < 2) {
          errors.push(`${fieldPathLabel} (Minimum 2 subnets allowed)`);
        }
        return;
      }

      if (field.isRequired && isEmpty) {
        errors.push(fieldPathLabel);
      }
    });

    return errors;
  };

  const isDependencyMet = (field, currentData, globalData) => {
    if (isAzureStorageAccount && isStorageDataProtectionField(field)) {
      return isStorageFieldVisible(field, formConfig, { ...globalData, ...currentData });
    }
    if (!field.dependentOn || String(field.dependentOn).trim() === "") return true;

    const depFields = String(field.dependentOn).split(",").map(s => s.trim()).filter(Boolean);

    const getActualVal = (depField) =>
      currentData?.[depField]?.value ?? globalData?.[depField]?.value;

    if (field.dependentValue == null || String(field.dependentValue).trim() === "") {
      return depFields.every((depField) => {
        const actualVal = getActualVal(depField);

        // Must exist and not be empty
        if (actualVal === undefined || actualVal === null || actualVal === "") return false;

        if (actualVal === false || String(actualVal).toLowerCase() === "false") return false;

        return true;
      });
    }

    const expectedValues = String(field.dependentValue)
      .split(",")
      .map(s => s.trim().toLowerCase());

    const primaryDepVal = getActualVal(depFields[0]);

    if (primaryDepVal === undefined || primaryDepVal === null || primaryDepVal === "") return false;

    return expectedValues.includes(String(primaryDepVal).toLowerCase());
  };

  const checkIntegrations = async () => {
    const response = await api.get("/api/Resources/IntegrationStatus", {
      params: {
        node: "snow"
      },
      headers: { accept: "application/json" },
    });

    const { overallHealthy, integrations } = response.data;

    if (!overallHealthy) {
      const messages = integrations
        ?.filter(i => !i.isHealthy)
        .map(i => `${i.name}: ${i.message || "Integration unavailable"}`);

      const err = new Error(messages?.join("\n") || "One or more integrations failed.");
      err.isIntegration = true;
      throw err;
    }
  };



  const validateEc2 = async (formData, resourceTypeString, resourceId, useSuffix = false, listeners = [], rules = []) => {
  const type = resourceTypeString?.toLowerCase() || "";
  if (
    !type.includes("ec2") &&
    !type.includes("security groups") &&
    !type.includes("s3 bucket") &&
    !type.includes("alb") &&
    !type.includes("nlb") &&
    !type.includes("route 5") &&
    !type.includes("rds") &&
    !type.includes("db_instance") &&
    !type.includes("database")
  ) {
    console.warn("--> Validation SKIPPED for type:", type);
    return;
  }

  const subnetId = Number(formData.subnet?.value);
  const instanceTypeId = Number(formData.Ec2instance?.value);
  const awsAmiId = formData.AwsAmis?.value;
  const accountId = localStorage.getItem("selectedAccountId");
  const vpcId = formData.vpcId?.value;

  const inboundRules = (rules?.InboundRules || []).map(rule =>
    Object.fromEntries(
      Object.entries(rule)
        .filter(([key]) => key !== "__meta")
        .map(([key, value]) => [key, value?.value ?? ""])
    )
  );

  const outboundRules = (rules?.OutboundRules || []).map(rule =>
    Object.fromEntries(
      Object.entries(rule)
        .filter(([key]) => key !== "__meta")
        .map(([key, value]) => [key, value?.value ?? ""])
    )
  );

  let actualFormResourceName = formData["resourceName"]?.value || formData["Name"]?.value || "";

  // FIX FOR ROUTE53
  if (typeof resourceTypeString === "string" &&
    resourceTypeString.toLowerCase().includes("route 53")) {

    const resourceRequestData = buildResourceRequestData(
      formConfig,
      formData,
      dropdownData,
      [],
      activeAccountId
    );

    actualFormResourceName = resourceRequestData.RecordName;
  }

  if (useSuffix) {
    actualFormResourceName = `${actualFormResourceName}-${Math.floor(Math.random() * 10000)}`;
  }

  if (type.includes("ec2")) {
    if (!Number.isInteger(subnetId)) throw new TypeError("Subnet is required for EC2.");
    if (!Number.isInteger(instanceTypeId)) throw new TypeError("Invalid Instance Type selected.");
    if (!awsAmiId) throw new TypeError("AMI is required.");
  }

  let validationParams = {};

  if (type.includes("rds")) {
    validationParams = {
      accountId: accountId,
      resourceName: actualFormResourceName,
      resourceType: "rds",
      VpcId: vpcId || 0,
    };

    formConfig.forEach((field) => {
      if (field.dependentOn && field.dependentValue) {
        const rawParentValue = formData[field.dependentOn]?.value;
        const expectedValues = String(field.dependentValue).split(',');
        
        if (!expectedValues.includes(String(rawParentValue))) {
          return; 
        }
      }

      const raw = formData[field.fieldName]?.value;
      if (raw === undefined || raw === null || raw === "") return;

      let safeVal = raw;
      if (typeof raw === "object" && !Array.isArray(raw)) {
        safeVal = raw.value ?? raw.label ?? raw.text ?? "";
      } else if (Array.isArray(raw)) {
        safeVal = raw.map(r => typeof r === "object" ? (r.value ?? r) : r).join(",");
      }

      if (safeVal && validationParams[field.fieldName] === undefined) {
        validationParams[field.fieldName] = safeVal;
      }
    });
  } else if (type.includes("ec2")) {
    validationParams = {
      subnetId,
      instanceTypeId,
      accountId,
      awsAmiId,
      resourceName: actualFormResourceName,
      resourceId,
      resourceType: resourceTypeString
    };
  } else {
    validationParams = {
      resourceName: actualFormResourceName,
      resourceId,
      accountId,
      resourceType: resourceTypeString,
      ...(type.includes("security group") ? {
        vpcId,
        InboundRules: JSON.stringify(inboundRules || []),
        OutboundRules: JSON.stringify(outboundRules || [])
      } : {}),
      ...(type.includes("alb") || type.includes("nlb") ? {
        listeners: JSON.stringify(listeners)
      } : {})
    };
  }
  
  const { data } = await api.get(
    "/api/AwsEc2Data/ValidateResourceCreation",
    {
      params: validationParams,
      headers: {
        "X-CORRELATION-ID": crypto.randomUUID(),
        "accept": "application/json"
      }
    });

  if (!data?.isValid) {
    const err = new Error(data?.reason || "Validation failed.");
    err.showDialog = true;
    err.fieldName = data?.fieldName || "";
    throw err;
  }
};

  const updateTemplateLoading = (title, message, progress) => {
    setLoadingTitle(title);
    setLoadingMessage(message);
    setLoadingProgress(progress);
  };

  const shouldRunTabServerValidation = (currentTabFields, resourceTypeString) => {
    const type = resourceTypeString?.toLowerCase() || "";
    const currentFieldNames = new Set(currentTabFields.map((field) => field.fieldName));

    if (type.includes("ec2")) {
      return ["resourceName", "subnet", "Ec2instance", "AwsAmis"].every((fieldName) =>
        currentFieldNames.has(fieldName)
      );
    }

    if (type.includes("rds")) {
      return currentFieldNames.has("resourceName") || currentFieldNames.has("Name");
    }

    if (type.includes("security groups") || type.includes("s3 bucket")) {
      return currentFieldNames.has("resourceName") || currentFieldNames.has("Name");
    }

    return false;
  };

  const mapValidationFieldsToCurrentTab = (rawFieldNames, currentTabFields) => {
    const currentFieldNames = currentTabFields.map((field) => field.fieldName);
    const lowerToActual = new Map(
      currentFieldNames.map((fieldName) => [fieldName.toLowerCase(), fieldName])
    );

    const aliasGroups = [
      ["resourcename", "name"],
    ];

    const resolved = new Set();

    (rawFieldNames || [])
      .map((fieldName) => fieldName?.trim())
      .filter(Boolean)
      .forEach((fieldName) => {
        const normalized = fieldName.toLowerCase();

        if (lowerToActual.has(normalized)) {
          resolved.add(lowerToActual.get(normalized));
          return;
        }

        const aliasGroup = aliasGroups.find((group) => group.includes(normalized));
        if (!aliasGroup) {
          return;
        }

        const matchingAlias = aliasGroup
          .map((alias) => lowerToActual.get(alias))
          .find(Boolean);

        if (matchingAlias) {
          resolved.add(matchingAlias);
        }
      });

    return Array.from(resolved);
  };


  const normalizeCollectionValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => {
        if (item && typeof item === "object") {
          const normalizedRow = {};

          Object.entries(item).forEach(([key, fieldObj]) => {
            if (key === "__meta") { return; }
            const raw = fieldObj?.value;

            if (Array.isArray(raw)) {
              if (raw.length > 0 && typeof raw[0] === "object") {
                normalizedRow[key] = normalizeCollectionValue(raw);
              } else {
                normalizedRow[key] = raw.join(",");
              }
            } else if (raw && typeof raw === "object") {
              normalizedRow[key] =
                raw.value ?? raw.label ?? raw.text ?? "";
            } else {
              normalizedRow[key] = raw ?? "";
            }
          });

          return normalizedRow;
        }
        return item;
      });
    }

    return value;
  };

  const buildResourceRequestData = (
  formConfig,
  formData,
  dropdownData,
  additionalTags,
  activeAccountId
) => {
  const orgName = sessionStorage.getItem("organizationName") || "Gainwell Technologies";
  const data = {
    ActiveAccountId: activeAccountId,
    Customer: orgName,
    Company: orgName,
    ManagedBy: "NTT",
    CreatedBy: "CMP",
  };

  formConfig.forEach((field) => {
    const isStorageField = isAzureStorageAccount;
    if (isStorageField) {
      if (!isStorageFieldVisible(field, formConfig, formData)) return;
    } else if (field.dependentOn && field.dependentValue) {
      const parentVal = formData[field.dependentOn]?.value;
      if (parentVal !== field.dependentValue) {
        return;
      }
    }
    const fieldState = formData[field.fieldName];
    const raw = isStorageField
      ? getStorageFieldValue(field, formData)
      : formData[field.fieldName]?.value;
    const label = fieldState?.label;
    if (raw === undefined || raw === null || raw === "") return;

    let safe;

    if (field.fieldType === "collection") {
      safe = normalizeCollectionValue(raw);
    } else if (Array.isArray(raw)) {
      if (raw.length > 0 && typeof raw[0] === "object") {
        safe = normalizeCollectionValue(raw);
      } else {
        safe = raw.join(",");
      }
    } else if (typeof raw === "object") {
      safe = raw.value ?? raw.label ?? raw.text ?? "";
    } else {
      safe = raw;
    }

    if (
      resourceName === "Route 53" &&
      field.fieldName === "RecordName" &&
      safe
    ) {
      const hostedZone = (dropdownData.HostedZones || []).find(
        (z) => String(z.value) === String(formData.HostedZones?.value)
      );

      const hostedZoneText = hostedZone?.text || hostedZone?.label || "";

      if (hostedZoneText) {
        safe = `${safe}.${hostedZoneText}`.replace(/\.\.+/g, ".");
      }
    }


    if (field.fieldName.startsWith("Ivanti")) {
      if (!data.ivantiConfiguration) {
        data.ivantiConfiguration = {};
      }
      if (field.fieldType === "multiSelectDropdown") {
        const selectedIds = Array.isArray(raw) ? raw : [raw];
        const options = dropdownData[field.fieldName] || [];
        
        data.ivantiConfiguration[field.fieldName] = selectedIds.map(id => {
          const opt = options.find(o => String(o.value) === String(id));
          return { 
            value: String(id), 
            text: opt ? (opt.label || opt.text) : String(id) 
          };
        });
      } else {
        data.ivantiConfiguration[field.fieldName] = { 
          value: String(safe), 
          text: label 
        };
      }
      
      return;
    }

    data[field.fieldName] = safe;
  });

  if (formData.environment?.value) {
    const env = dropdownData.environment?.find(
      (e) => e.value === formData.environment.value
    );
    data.Environment = env?.text || formData.environment.label;
  }

  if (formData.resourceName?.value && !isAzureVirtualMachine) {
    data.Name = formData.resourceName.value;
  }

  additionalTags.forEach((tag) => {
    if (tag.key && tag.value) {
      data[tag.key] = `${tag.value}|additionalTagsTrue`;
    }
  });

  // Remove subscriptionId from the nested resourceRequest
  for (const key of Object.keys(data)) {
    if (key.toLowerCase() === "subscriptionid") {
      delete data[key];
    }
  }

  return data;

  return data;
};

const transformAzureVmPayload = (flatData, formData, dropdownData) => {
  if (!flatData) return flatData;
  
  const getValue = (fieldName) => formData[fieldName]?.value ?? formData[fieldName] ?? "";
  
  return {
    subscription_id: flatData.subscription,
    resource_group_name: getValue("resourceGroup") || getValue("ResourceGroup"),
    location: getValue("location"),
    
    basics: {
      vm_name: getValue("vmname") || getValue("resourceName") || getValue("Name"),
      
      availability: {
        type: getValue("availabilityZone") ? "AvailabilityZone" : "AvailabilitySet",
        zone: getValue("availabilityZone")
      },
      
      security_type: getValue("securityType"),
      
      image: {
        source: "Marketplace",
        publisher: getValue("publisher"),
        offer: getValue("offer"),
        sku: getValue("sku"),
        version: getValue("version") || "latest"
      },
      
      architecture: getValue("architecture"),
      vm_size: getValue("vmSizeName"),
      
      authentication: {
        type: getValue("authenticationType") || "UsernamePassword",
        username: getValue("username")
      },
      
      public_inbound_ports: getValue("publicInboundPorts") || "None"
    },
    
    disks: {
      encryption_at_host: getValue("encryptionAtHost") === "true" || getValue("encryptionAtHost") === true,
      
      os_disk: {
        size: getValue("osDiskSize"),
        redundancy: getValue("osDiskRedundancy") || "LRS",
        type: getValue("osDiskType") || "StandardSSD",
        delete_with_vm: true
      },
      
      enable_ultra_disk_compatibility: getValue("enableUltraDiskCompatibility") === "true",
      key_management: getValue("keyManagement") || "PlatformManaged"
    },
    
    networking: {
      vnet_resource_group: getValue("resourceGroup") || getValue("ResourceGroup"),
      vnet_name: getValue("virtualnetwork"),
      subnet_name: getValue("azureSubnetId"),
      
      public_ip: getValue("publicIp") || "None",
      nic_network_security_group: getValue("networkSecurityGroup") || "None",
      
      delete_nic_with_vm: true,
      accelerated_networking: getValue("acceleratedNetworking") === "true" || getValue("acceleratedNetworking") === true,
      
      load_balancing: getValue("loadBalancing") || "None"
    },
    
    management: {
      microsoft_entra_login: getValue("microsoftEntraLogin") === "true",
      
      identity: {
        system_assigned: getValue("systemAssignedIdentity") === "true"
      },
      
      guest_os_updates: getValue("guestOsUpdates") !== "false"
    },
    
    monitoring: {
      boot_diagnostics: {
        enabled: getValue("bootDiagnostics") !== "false",
        storage_type: getValue("bootDiagnosticsStorageType") || "Managed"
      }
    },
    
    tags: Object.keys(flatData)
      .filter(key => key.startsWith("tag_") || key.startsWith("Std_") || key === "CreatedBy")
      .reduce((tags, key) => {
        tags[key.replace("tag_", "")] = flatData[key];
        return tags;
      }, flatData.tags || {})
  };
};

  const calculateApproval = ({
    selectedProvider,
    awsPrice,
    priceDetails,
    resourceName,
    formData,
    budgetLimit,
  }) => {
    const forecasted =
      selectedProvider?.toUpperCase() === "AWS"
        ? awsPrice?.discountPricePerMonth || 0
        : priceDetails?.discountPricePerMonth || 0;

    let isApprovedRequest = "false";
    const type = resourceName.toLowerCase();

    if (type.includes("ec2")) {
      if (
        formData.availabilityType?.value === "Private" &&
        forecasted <= budgetLimit
      ) {
        isApprovedRequest = "true";
      }
    } else if (type.includes("s3") || type.includes("security")) {
      if (forecasted <= budgetLimit) {
        isApprovedRequest = "true";
      }
    }

    return {
      totalPrice: forecasted,
      forecastedPricePerMonth: forecasted,
      isApprovedRequest,
    };
  };


  const preloadForEditOrClone = async (mode, recordId, cloned) => {
    try {
      setLoading(true);

      const resp = await api.get(`/api/Resources/GetResourceRequestDetailsByResourceId`, {
        params: { resourceId: recordId, clonedResource: !!cloned },
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "text/plain",
        }
      });

      const rows = Array.isArray(resp.data) ? resp.data : [];
      if (!rows.length) {
        toast.info("No details found");
        setLoading(false);
        return;
      }

      const nextFormData = {};
      const restoredAdditionalTags = [];
      const nextDisabledFields = {};

      rows.forEach(r => {
        let fieldName = r.fieldName;

        if (fieldName === "securityGroupId") fieldName = "securityGroupID";
        if (fieldName === "operatingSystem") fieldName = "Operating_system";
        nextDisabledFields[fieldName] = cloned ? false : r.isDisable;

        if (r.isAdditionalTag === true) {


          restoredAdditionalTags.push({
            id: crypto.randomUUID(),
            key: fieldName,
            value: (r.fieldValue ?? "").replace("|additionalTagsTrue", "")
          });
          return;
        }

        let value = r.fieldValue;
        let label = r.fieldData ?? r.fieldValue;



        if (
          fieldName === "InboundRules" ||
          fieldName === "OutboundRules"
        ) {
          try {
            const parsedRules = JSON.parse(r.fieldValue);

            const transformedRules = parsedRules.map(rule => {
              const transformed = {};

              // Object.entries(rule).forEach(([key, val]) => {
              //   transformed[key] = {
              //     value: val ?? "",
              //     label: val ?? ""
              //   };
              // });


              Object.entries(rule).forEach(([key, val]) => {

                if (key === "IsExistingRule") return;

                transformed[key] = {
                  value: val ?? "",
                  label: val ?? ""
                };
              });

              transformed.__meta = {
                isExisting: rule.IsExistingRule === true,
                isEditable: rule.IsEditable
              };

              return transformed;
            });

            nextFormData[fieldName] = {
              value: transformedRules,
              label: `${transformedRules.length} rules`
            };

            return;
          } catch (err) {
            console.error("Rules parse failed", err);
          }
        }


        if (typeof value === "string" && value.trim().startsWith("[")) {
          try {
            value = JSON.parse(value);
            label = Array.isArray(value) ? value.join(", ") : value;
          } catch { }
        }

        const fieldConfig = formConfig.find(f => f.fieldName === fieldName);

        if (value !== null && value !== undefined && !Array.isArray(value)) {
          value = String(value);
        }

        if (cloned && (fieldName === "resourceName" || fieldName === "Name")) {
          value = "";
          label = "";
        }

        if (fieldConfig?.fieldType === "multiSelectDropdown") {
          let valuesArray = [];
          if (Array.isArray(value)) {
            valuesArray = value.map(String);
          } else if (typeof value === "string" && value.includes(",")) {
            valuesArray = value.split(",").map(v => v.trim()).filter(Boolean);
          } else if (value) {
            valuesArray = [String(value)];
          }

          nextFormData[fieldName] = {
            value: valuesArray,
            label: Array.isArray(label) ? label.join(", ") : String(label ?? ""),
          };
        } else {
          nextFormData[fieldName] = {
            value,
            label: String(label ?? value ?? ""),
          };
        }
      });

      if (isAzure) {
        Object.assign(nextFormData, getAzureFormDefaults(formConfig));
      }

      const dependencyChain = [
        "location",
        "environment",
        "platformDetails",
        "Operating_system",
        "Specific_Type",
        "Ec2instance",
        "vpcId",
        "subnet",
        "securityGroupID",
        "AwsAmis",
        "Keypair",
      ];

      for (const fieldName of dependencyChain) {
        if (nextFormData[fieldName]?.value) {
          const fieldConfig = formConfig.find(f => f.fieldName === fieldName);
          if (fieldConfig?.dropdownType === "api") {
            await fetchDropdownData(fieldName, { ...formData, ...nextFormData });
            await new Promise(resolve => setTimeout(resolve, 150));
          }
        }
      }

      setAdditionalTags(restoredAdditionalTags);
      setDisabledFields(nextDisabledFields);
      setFormData(prev => ({ ...prev, ...nextFormData }));

      toast.success(cloned ? "Cloned values loaded" : "Edit values loaded");
    } catch (err) {
      console.error("Preload error:", err);
      toast.error("Failed loading resource data");
    } finally {
      setLoading(false);
    }
  };

  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");
  const [steps, setSteps] = useState({
    integration: {
      status: "pending",
      desc: "Waiting to start..."
    },
    provisioning: {
      status: "pending",
      desc: "Waiting to start..."
    }
  });
  const [validationSource, setValidationSource] = useState("");
  const runValidationApi = async () => {
    setApiValidationFields([]);

    const missingRequired = formConfig.filter(field =>
      field.isDisplay && field.isRequired && !formData[field.fieldName]?.value
    );

    if (missingRequired.length > 0) {
      setApiValidationFields(missingRequired.map(f => f.fieldName));
      toast.error("Please fill in all mandatory fields before submitting.");
      return false;
    }

    try {
      const payload = Object.keys(formData).reduce((acc, key) => {
        acc[key] = formData[key].value;
        return acc;
      }, {});

      await api.post(`/api/Resources/validate`, payload);
      return true;
    } catch (error) {
      if (error.response?.status === 405 || error.response?.status === 404) {
        console.warn(`Validation API ${error.response.status} - Skipping silent check.`);
        return true;
      }

      console.error("Validation API Error:", error);
      return false;
    }
  };

const validateAzureResource = async (azureValidationResourceId) => {
  if (isAzureVirtualMachine) {
    return validateAzureVmCreation({
      formConfig,
      formData,
      accountId: activeAccountId,
      resourceId: azureValidationResourceId,
    });
  }
  const validationField = formConfig.find(isAzureResourceGroupNameField);

  if (!validationField) {
    throw new Error("Azure resource group field was not found.");
  }

  const validationName = String(
    formData[validationField.fieldName]?.value ?? ""
  ).trim();

  const nameError = getResourceGroupNameError(validationName);
  if (nameError) {
    toast.error(nameError);
    return false;
  }
  const validationParams = {
    resourceName: validationName,
    resourceId :azureValidationResourceId,
    accountId: activeAccountId,
    resourceType: resourceName,
  };

  const { data } = await api.get(
    "/api/AwsEc2Data/ValidateResourceCreation",
    {
      params: validationParams,
      headers: {
        "X-CORRELATION-ID": crypto.randomUUID(),
        accept: "application/json",
      },
    }
  );

  if (!data?.isValid) {
    const error = new Error(data?.reason || "Validation failed.");
    error.showDialog = true;
    error.fieldName = data?.fieldName || "";
    throw error;
  }

  return true;
};


  const validateSecurityGroup = () => {
    setSgTouched(true);
    const { errors } = validateSecurityGroupRules(
      formConfig,
      formData
    );
    const { isValid, error } = validateSGForNext(
      formData
    );

    if (!isValid) {
      toast.error(error);
      return false;
    }

    if (Object.keys(errors).length > 0) {
      toast.error("Fix highlighted Security Group errors");
      return false;
    }

    return true;
  };


  const validateVmBeforeNavigation = (fields) => {
    if (!isAzureVirtualMachine) return true;
    const errors = validateAzureVmFields(
      fields, formData, (field, data) => !shouldHideField(field, data), formConfig
    );
    if (!errors.length) return true;
    const first = errors[0];
    const tabIndex = tabNames.indexOf(first.field.tabName);
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
      setValidatedTab(tabIndex);
    }
    toast.error(first.message);
    return false;
  };

 const handleSubmit = async (event, useSuffix = false) => {
    if (event) event.preventDefault();

    setFormSubmitted(true);
    if (!validateVmBeforeNavigation(formConfig)) return;

if (isAzureStorageAccount) {
  const result = await validateAzureStorageAccountForm({
    formConfig,
    formData,
    shouldHideField,
    checkAvailability: true,
    accountId: activeAccountId,
    resourceId: azureValidationResourceId,
    resourceType: resourceName,
  });

  if (!result.isValid) {
    const firstError = result.errors[0] || {};
    const errorField = formConfig.find(
      (field) => field.fieldName === firstError.fieldName
    );
    const errorTabIndex = tabNames.indexOf(errorField?.tabName);
    const scopedFields = Array.from(
      new Set(
        (result.errors || [])
          .map((error) => error?.fieldName)
          .filter(Boolean)
      )
    );

    if (errorTabIndex !== -1) {
      setActiveTab(errorTabIndex);
      setValidatedTab(errorTabIndex);
    }

    setValidationReason(firstError.message || "Validation failed.");
    setApiValidationFields(scopedFields);
    setIsDuplicateNameValidation(false);
    setOpenValidationDialog(true);
    return;
  }
}

    const resourceGroupField = formConfig.find(
  isAzureResourceGroupNameField
);

if (resourceGroupField && !isAzureVirtualMachine) {
  const nameError = getResourceGroupNameError(
    formData[resourceGroupField.fieldName]?.value
  );

  if (nameError) {
    const errorTabIndex = tabNames.indexOf(resourceGroupField.tabName);

    if (errorTabIndex !== -1) {
      setActiveTab(errorTabIndex);
    }

    toast.error(nameError);
    return;
  }
}


    try {
      const isListenersValid = listenersRef.current
        ? listenersRef.current.validate()
        : true;

      if (resourceName?.toLowerCase().includes("security")) {
        if (!validateSecurityGroup()) {
          return;
        }
      }

      const confirmField = !isAzureVirtualMachine && formConfig.find(f => (f.matchWith || f.fieldName === "confirmMasterPassword") && f.isDisplay);
      if (confirmField) {
        const targetFieldName = confirmField.matchWith || "masterPassword";
        const originalVal = formData[targetFieldName]?.value;
        const confirmVal = formData[confirmField.fieldName]?.value;

        if (!confirmVal || originalVal !== confirmVal) {
          setApiValidationFields([confirmField.fieldName]);
          setApiFieldErrorText(!confirmVal ? "Please confirm your password." : "Passwords do not match.");
          toast.error(!confirmVal ? "Confirm Password is required." : `${confirmField.fieldLabel || "Passwords"} must match the Master Password.`);
          const errorTabIndex = tabNames.indexOf(confirmField.tabName);
          if (errorTabIndex !== -1) setActiveTab(errorTabIndex);
          return;
        }
      }

      const masterUsernameField = !isAzureVirtualMachine && formConfig.find(f => f.fieldName === "masterUsername" && f.isDisplay);
      if (masterUsernameField) {
        const uVal = formData.masterUsername?.value;
        const uError = getUsernameError(uVal);
        if (uError) {
          setApiValidationFields(["masterUsername"]);
          setApiFieldErrorText(uError);
          toast.error("Fix Master Username constraints before continuing.");
          const errorTabIndex = tabNames.indexOf(masterUsernameField.tabName);
          if (errorTabIndex !== -1) setActiveTab(errorTabIndex);
          return; 
        }
      }

      const masterPasswordField = !isAzureVirtualMachine && formConfig.find(f => f.fieldName === "masterPassword" && f.isDisplay);
      if (masterPasswordField) {
        const pwdVal = formData.masterPassword?.value;
        const pwdError = getPasswordError(pwdVal);
        if (pwdError) {
          setApiValidationFields(["masterPassword"]);
          setApiFieldErrorText(pwdError);
          toast.error("Fix Master Password constraints before continuing.");
          const errorTabIndex = tabNames.indexOf(masterPasswordField.tabName);
          if (errorTabIndex !== -1) setActiveTab(errorTabIndex);
          return; 
        }
      }

      if (masterPasswordField) {
        const pwdVal = formData.masterPassword?.value;
        const pwdError = getPasswordError(pwdVal);
        
        if (pwdError) {
          setApiValidationFields(["masterPassword"]);
          setApiFieldErrorText(pwdError);
          toast.error("Fix Master Password constraints before continuing.");
          
          const errorTabIndex = tabNames.indexOf(masterPasswordField.tabName);
          if (errorTabIndex !== -1) {
            setActiveTab(errorTabIndex);
          }
          return; 
        }
      }

      const missingFields = isAzureVirtualMachine || isAzureStorageAccount ? [] : validateRequiredFields(
        formConfig,
        formData
      );

      if (missingFields.length > 0 || !isListenersValid) {
        toast.error("Please fill in all required fields.");
        return;
      }

      const storageField = !isAzureVirtualMachine && formConfig.find(f =>
        (f.fieldLabel?.includes("Allocated Storage") || f.fieldName === "allocatedStorage" || f.fieldName === "AllocatedStorage") && f.isDisplay
      );
      
      if (storageField) {
        const storageVal = Number(formData[storageField.fieldName]?.value || 0);
        
        if (storageVal < 20 || storageVal > 65536) {
          setApiValidationFields([storageField.fieldName]);
          setApiFieldErrorText("Must be between 20 and 65,536 GiB.");
          toast.error("Allocated Storage must be between 20 and 65,536 GiB.");
          
          const errorTabIndex = tabNames.indexOf(storageField.tabName);
          if (errorTabIndex !== -1) {
            setActiveTab(errorTabIndex);
          }
          return; // Blocks the form from submitting!
        }
      }
      const invalidRedirectUrl = formData.Listeners?.value?.find((listener) => {
        const routingAction = listener?.RoutingActions?.value;
        const redirectUrl = listener?.RedirectFullURL?.value;

        if (routingAction !== "RedirectToURL") {
          return false;
        }

        return !isValidAlbRedirectUrl(redirectUrl);
      });

      if (invalidRedirectUrl) {
        toast.error("Enter a valid URL (e.g., https://example.com/path)");
        return;
      }

      const tgErrors = [];

      const isNLB = resourceName?.toLowerCase().includes("nlb");

      formData.Listeners?.value?.forEach((listener, idx) => {
        const rows = listener?.TargetGroupCollection?.value || [];

        if (isNLB) {
          if (!rows[0]?.TargetGroup?.value) {
            tgErrors.push(`Listener ${idx + 1} - Target group required`);
          }
          return;
        }

        if (listener?.RoutingActions?.value === "ForwardToTargetGroups") {
          const rows = listener?.TargetGroupCollection?.value || [];

          if (rows.length === 0) {
            tgErrors.push(`Listener ${idx + 1} - Target group missing`);
          }

          rows.forEach((row, rIdx) => {
            if (!row || !row.TargetGroup || !row.TargetGroup.value) {
              tgErrors.push(`Listener ${idx + 1} - Target group required`);
            }
          });
        }
      });

      if (tgErrors.length > 0) {
        toast.error("Target group is required");
        return;
      }

      const rulesPayload = getSecurityGroupRulesPayload(formData);

      const tlsErrors = [];

      formData.Listeners?.value?.forEach((listener, index) => {
        const protocol = listener?.Protocol?.value;

        if (protocol === "TLS" || protocol === "HTTPS") {
          if (!formData.SecurityCategory?.value) {
            tlsErrors.push(`Listener ${index + 1} - Security Category`);
          }

          if (!formData.PolicyName?.value) {
            tlsErrors.push(`Listener ${index + 1} - Policy Name`);
          }

          if (!formData.CertificateSource?.value) {
            tlsErrors.push(`Listener ${index + 1} - Certificate Source`);
          }

          if (!formData.CertificateFromACM?.value) {
            tlsErrors.push(`Listener ${index + 1} - Certificate`);
          }
        }
      });

      if (tlsErrors.length > 0) {
        toast.error("Please fill Secure Listener fields");
        return;
      }

if (isAzure && !isAzureStorageAccount && (isAzureVirtualMachine || resourceGroupField)) {
  if (
    (mode === "edit" || mode === "clone" || clonedResource) &&
    !azureValidationResourceId
  ) {
    toast.error("The source resource ID is missing for edit/clone validation.");
    return;
  }

  setSubmitLoading(true);
  setValidationSource("submit");
  const validationPassed = await validateAzureResource(
    azureValidationResourceId
  );

  if (!validationPassed) {
    return;
  }
}


      setSubmitLoading(true);
      setValidationSource("submit");

      if (!useSuffix && !skipValidation) {
        await validateEc2(
          formData,
          resourceName,
          mode === "edit" ? editResourceId : 0,
          false,
          getListenersPayload(),
          rulesPayload
        );
      }

      setSubmissionOpen(true);
      setProgress(10);

      setSteps({
        integration: {
          status: "loading",
          desc: "Checking ServiceNow connectivity..."
        },
        provisioning: {
          status: "pending",
          desc: "Waiting to start..."
        }
      });

      /* ---------------------------------- */
      /* STEP 3 : CHECK INTEGRATION         */
      /* ---------------------------------- */

      let snowUp = true;

      try {
        await checkIntegrations();

        setSteps(prev => ({
          ...prev,
          integration: {
            status: "success",
            desc: "ServiceNow is available."
          }
        }));
      } catch (error) {
        snowUp = false;

        setSteps(prev => ({
          ...prev,
          integration: {
            status: "warning",
            desc:
              "ServiceNow is currently unavailable. Request will be queued."
          }
        }));
      }

      setProgress(45);

      /* ---------------------------------- */
      /* BUILD EXISTING REQUEST BODY        */
      /* ---------------------------------- */

      const requester =
        sessionStorage.getItem("userId");

      const subscription =
        selectedProvider?.toUpperCase() === "AWS"
          ? activeAccountId
          : activeAccountId;

      const region =
        formData.location?.value ||
        "southafricanorth";

      const resourceRequestData =
        buildResourceRequestData(
          formConfig,
          formData,
          dropdownData,
          additionalTags,
          activeAccountId
        );

      const listeners = formData.Listeners?.value?.map(listener => {
        const routingAction = listener?.RoutingActions?.value;

        const isALB = resourceName?.toLowerCase().includes("alb");

        let safeListener = { ...listener };

        const isStickinessEnabled =
          listener?.TargetGroupStickiness?.value === true ||
          String(listener?.TargetGroupStickiness?.value).toLowerCase() === "true";

        if (isALB && (!isStickinessEnabled || routingAction !== "ForwardToTargetGroups")) {
          delete safeListener.TargetGroupStickiness;
          delete safeListener.TargetGroupStickinessDuration;
        }

        if (routingAction !== "RedirectToURL") {
          delete safeListener.RedirectStatusCode;
        }

        if (routingAction === "ForwardToTargetGroups" || routingAction === "RedirectToURL") {
          delete safeListener.ContentType;
        }

        return Object.fromEntries(
          Object.entries(safeListener)
            .map(([k, v]) => {
              if (k === "TargetGroupStickiness" && v?.value === false) {
                return null;
              }

              if (Array.isArray(v?.value)) {
                const cleanedArray = v.value.map(item => {
                  return Object.fromEntries(
                    Object.entries(item)
                      .map(([nk, nv]) => [nk, nv?.value])
                      .filter(([_, nv]) => nv !== "" && nv !== undefined && nv !== null)
                  );
                });

                return [k, cleanedArray];
              }

              return [k, v?.value];
            })
            .filter(Boolean)
            .filter(([_, v]) => v !== "" && v !== undefined && v !== null)
        );
      }) || [];

      const isLoadBalancer =
        resourceName?.toLowerCase().includes("alb") ||
        resourceName?.toLowerCase().includes("nlb");

      if (isLoadBalancer && listeners.length > 0) {
        resourceRequestData.Listeners = listeners;
      } else {
        delete resourceRequestData.Listeners;
      }

      const {
        totalPrice,
        isApprovedRequest
      } = calculateApproval({
        selectedProvider,
        awsPrice,
        priceDetails,
        resourceName,
        formData,
        budgetLimit
      });

      const isRoute53 = resourceName?.toLowerCase().includes("route 53");

      const route53RecordName = getRoute53FullName(formData);
      const azureResourceGroupField = formConfig.find((field) =>
        isAzureResourceGroupNameField(field)
      );
      const azureResourceGroupName =
        azureResourceGroupField
          ? renderSafeText(
            formData[azureResourceGroupField.fieldName]?.value
          ).trim()
          : "";

const azureStorageNameField = isAzureStorageAccount
  ? formConfig.find((field) => isAzureStorageNameField(field.fieldName))
  : null;

const azureStorageAccountName = azureStorageNameField
  ? renderSafeText(
      formData[azureStorageNameField.fieldName]?.value
    ).trim()
  : "";


      const requestBody = {
        applicationName: isRoute53
          ? route53RecordName
          : formData.ApplicationName?.value || " ",

        applicationId: isRoute53
          ? route53RecordName
          : formData.applicationId?.value || " ",

        subscription,
        // subscriptionId: subscription,
        requester: String(requester),
        region,
        totalPrice,

        marketPrice:
          selectedProvider?.toUpperCase() ===
            "AWS"
            ? awsPrice?.pricePerMonth || 0
            : priceDetails?.marketRatePerMonth ||
            0,

        resourceRequest: JSON.stringify(
          isAzureVirtualMachine ? transformAzureVmPayload(resourceRequestData, formData, dropdownData) : resourceRequestData
        ),

        resourceType: String(resourceId),

        OrganizationId: String(
          sessionStorage.getItem(
            "organizationId"
          )
        ),

        cloudProvider: String(cloudProvider),

        Environment:
          formData.environment?.value || "",

        resourceNameConstraint: isRoute53
          ? route53RecordName
           : isAzureStorageAccount
  ? azureStorageAccountName
          : (isAzure && azureResourceGroupName)
          ? azureResourceGroupName
          : formData.resourceName?.value ||
          formData.Name?.value ||
          "",

        isApprovedRequest,
        priority:
          formData.priority?.value || "0",

        changeResourceName: useSuffix
      };

      let url =
        mode === "edit" && editResourceId
          ? "/api/Resources/UpdateResourceDetail"
          : "/api/Resources/createResource";

      /* ---------------------------------- */
      /* STEP 4 : CREATE RESOURCE           */
      /* ---------------------------------- */

      setSteps(prev => ({
        ...prev,
        provisioning: {
          status: "loading",
          desc: "Submitting resource request..."
        }
      }));

      const response = await api.post(
        url,
        requestBody,
        mode === "edit"
          ? {
            params: {
              resourceId: editResourceId
            },
            headers: {
              accept: "application/json",
              "X-CORRELATION-ID":
                crypto.randomUUID()
            }
          }
          : {
            headers: {
              accept: "application/json",
              "X-CORRELATION-ID":
                crypto.randomUUID()
            }
          }
      );

      await delay(400);
     const apiMessage =
        response?.data?.message ??
        response?.data?.data?.message ??
        response?.data?.result?.message ??
        response?.data?.statusMessage ??
        "";
 
      setSuccessMessage(
        typeof apiMessage === "string" ? apiMessage : String(apiMessage)
      );

      setProgress(100);

     setSteps(prev => ({
        ...prev,
        provisioning: {
          status: snowUp ? "success" : "warning",
          desc: snowUp
            ? "Resource created successfully."
            : "Request accepted. Resource will be created automatically once ServiceNow is available."
        }
      }));
      
      setTimeout(() => {
        setSubmissionOpen(false);
        navigate("/provision-status");
      }, 3000);
    } catch (error) {
      console.error(error);

      /* Existing validation popup (duplicate name etc.) */
      if (error.showDialog) {
        const msg = error.message || "";

        const isDuplicateName =
          msg.toLowerCase().includes("already exists");

        const fields = error.fieldName
          ? error.fieldName.split(",").map(f => f.trim())
          : [];

        setValidationReason(msg);
        setApiValidationFields(fields);
        setIsDuplicateNameValidation(isDuplicateName && !isAzureVirtualMachine);
        setOpenValidationDialog(true);

        return;
      }

      /*  Show error INSIDE Submission Popup */
      setSteps(prev => ({
        ...prev,
        provisioning: {
          status: "error",
          desc: error.message || "Submission failed. Please try again."
        }
      }));

      setProgress(100);

      /*  Optional: auto close after few seconds */
      setTimeout(() => {
        setSubmissionOpen(false);
      }, 3000);
    }
    finally {
      setSubmitLoading(false);
      setSkipValidation(false);
    }
  };

  const [nextMenuAnchor, setNextMenuAnchor] = useState(null);
  const [useSuffixMode, setUseSuffixMode] = useState(false);
  const [skipValidation, setSkipValidation] = useState(false);
  const handleNextMainClick = (event) => {
    setNextMenuAnchor(event.currentTarget);
  };

  const getListenersPayload = () => {
    return (
      formData.Listeners?.value?.map(listener => {
        const routingAction = listener?.RoutingActions?.value;

        const isALB = resourceName?.toLowerCase().includes("alb");

        let safeListener = { ...listener };

        const isStickinessEnabled =
          listener?.TargetGroupStickiness?.value === true ||
          String(listener?.TargetGroupStickiness?.value).toLowerCase() === "true";

        if (
          isALB &&
          (!isStickinessEnabled ||
            routingAction !== "ForwardToTargetGroups")
        ) {
          delete safeListener.TargetGroupStickiness;
          delete safeListener.TargetGroupStickinessDuration;
        }

        if (routingAction !== "RedirectToURL") {
          delete safeListener.RedirectStatusCode;
        }

        if (
          routingAction === "ForwardToTargetGroups" ||
          routingAction === "RedirectToURL"
        ) {
          delete safeListener.ContentType;
        }

        return Object.fromEntries(
          Object.entries(safeListener)
            .map(([k, v]) => {
              if (k === "TargetGroupStickiness" && v?.value === false) {
                return null;
              }

              if (Array.isArray(v?.value)) {
                const cleanedArray = v.value.map(item => {
                  return Object.fromEntries(
                    Object.entries(item)
                      .map(([nk, nv]) => [nk, nv?.value])
                      .filter(
                        ([_, nv]) =>
                          nv !== "" &&
                          nv !== undefined &&
                          nv !== null
                      )
                  );
                });

                return [k, cleanedArray];
              }

              return [k, v?.value];
            })
            .filter(Boolean)
            .filter(
              ([_, v]) =>
                v !== "" &&
                v !== undefined &&
                v !== null
            )
        );
      }) || []
    );
  };



  // const validationListeners =
  //   formData.Listeners?.value?.map(listener =>
  //     Object.fromEntries(
  //       Object.entries(listener)
  //         .map(([k, v]) => {
  //           if (Array.isArray(v?.value)) {
  //             return [
  //               k,
  //               v.value.map(item =>
  //                 Object.fromEntries(
  //                   Object.entries(item).map(([nk, nv]) => [
  //                     nk,
  //                     nv?.value
  //                   ])
  //                 )
  //               )
  //             ];
  //           }

  //           return [k, v?.value];
  //         })
  //     )
  //   ) || [];


 const handleNextWithValidation = async () => {
    setNextMenuAnchor(null); // Close menu
    const rulesPayload = getSecurityGroupRulesPayload(formData);
    const currentTabFields = formConfig.filter(f => f.tabName === tabNames[activeTab] && f.isDisplay);
    if (!validateVmBeforeNavigation(currentTabFields)) return;

    // 3. Standard Required Fields Check
    const missingFields = validateRequiredFields(currentTabFields, formData);
    if (missingFields.length > 0) {
      setValidatedTab(activeTab);
      toast.error("Please fill in required UI fields.");
      return;
    }

    setValidatedTab(null);
    setValidationSource("next");
    const shouldRunServerValidation =
      !useSuffixMode &&
      shouldRunTabServerValidation(currentTabFields, resourceName);

    if (shouldRunServerValidation) {
      setSubmitLoading(activeTab);
      try {
        await validateEc2(formData, resourceName, mode === "edit" ? editResourceId : 0, false, getListenersPayload(), rulesPayload);
        setActiveTab(prev => prev + 1);
      } catch (error) {
        if (error.showDialog) {
          const scopedFields = mapValidationFieldsToCurrentTab(
            error.fieldName?.split(",") || [],
            currentTabFields
          );
          const isDuplicateName = error.message.toLowerCase().includes("already exists");

          if (
            isDuplicateName &&
            scopedFields.some((fieldName) => ["resourceName", "Name"].includes(fieldName))
          ) {
            setForceNameError(true);
          }

          setValidationReason(error.message);
          setApiValidationFields(scopedFields);
          setIsDuplicateNameValidation(isDuplicateName);
          setOpenValidationDialog(true);
        } else {
          toast.error(error.message);
        }
      } finally {
        setSubmitLoading(false);
      }
    } else {
      setActiveTab(prev => prev + 1);
    }
  };

  const handleNextWithoutValidation = async () => {
    setNextMenuAnchor(null);
    if (isAzureStorageAccount) {
  const result = await validateAzureStorageAccountForm({
    formConfig,
    formData,
    tabName: tabNames[activeTab],
    shouldHideField,
  });

  if (!result.isValid) {
    setValidatedTab(activeTab);
    toast.error(result.errors[0].message);
    return;
  }
}

    const currentTabFields = formConfig.filter(f => f.tabName === tabNames[activeTab] && f.isDisplay);
    if (isAzureVirtualMachine) {
      if (validateVmBeforeNavigation(currentTabFields)) setActiveTab(prev => prev + 1);
      return;
    }

    const masterUsernameField = currentTabFields.find(f => f.fieldName === "masterUsername");
    if (masterUsernameField) {
      const uVal = formData.masterUsername?.value;
      const uError = getUsernameError(uVal);
      if (uError) {
        setApiValidationFields(["masterUsername"]);
        setApiFieldErrorText(uError);
        toast.error("Fix Master Username constraints before continuing.");
        return; 
      }
    }

    const masterPasswordField = currentTabFields.find(f => f.fieldName === "masterPassword");
    if (masterPasswordField) {
      const pwdVal = formData.masterPassword?.value;
      const pwdError = getPasswordError(pwdVal);
      if (pwdError) {
        setApiValidationFields(["masterPassword"]);
        setApiFieldErrorText(pwdError);
        toast.error("Fix Master Password constraints before continuing.");
        return; 
      }
    }

    const storageField = currentTabFields.find(f => f.fieldLabel?.includes("Allocated Storage") || f.fieldName === "allocatedStorage" || f.fieldName === "AllocatedStorage");
      
      if (storageField) {
        const storageVal = Number(formData[storageField.fieldName]?.value || 0);
        if (storageVal < 20 || storageVal > 65536) {
          setApiValidationFields([storageField.fieldName]);
          setApiFieldErrorText("Must be between 20 and 65,536 GiB.");
          toast.error("Allocated Storage must be between 20 and 65,536 GiB.");
          
          const errorTabIndex = tabNames.indexOf(storageField.tabName);
          if (errorTabIndex !== -1) setActiveTab(errorTabIndex);
          return; 
        }
      }

    const confirmField = currentTabFields.find(f => f.matchWith || f.fieldName === "confirmMasterPassword");
    if (confirmField) {
      const targetFieldName = confirmField.matchWith || "masterPassword";
      const originalVal = formData[targetFieldName]?.value;
      const confirmVal = formData[confirmField.fieldName]?.value;
      
      if (!confirmVal) {
        setApiValidationFields([confirmField.fieldName]);
        setApiFieldErrorText("Please confirm your password.");
        toast.error("Confirm Password is required.");
        return;
      }
      if (originalVal !== confirmVal) {
        setApiValidationFields([confirmField.fieldName]);
        setApiFieldErrorText("Passwords do not match.");
        toast.error(`${confirmField.fieldLabel || "Passwords"} must match the Master Password.`);
        return; 
      }
    }

    setActiveTab(prev => prev + 1);
  };

  const handleNext = () => {

    if (tabNames[activeTab] === "Rules") {

      setSgTouched(true);

      const { errors } = validateSecurityGroupRules(formConfig, formData);
      const { isValid, error } = validateSGForNext(formData);

      if (!isValid) {
        toast.error(error);
        return;
      }

      if (Object.keys(errors).length > 0) {
        toast.error("Fix highlighted errors");
        return;
      }
    }


  };

  const handlePrevious = () => {
    if (activeTab > 0) {
      setActiveTab((prev) => prev - 1);
    }
  };

  const handleContinueWithSuffix = async () => {
    setSubmitLoading(true);
    const rulesPayload = getSecurityGroupRulesPayload(formData);
    try {
      await validateEc2(
        formData,
        resourceName,
        mode === "edit" ? editResourceId : 0,
        true,
        getListenersPayload(),
        rulesPayload
      );

      setOpenValidationDialog(false);

      //  CRITICAL: mark validation as skipped
      setSkipValidation(true);

      if (validationSource === "next") {
        setUseSuffixMode(true);
        setActiveTab((prev) => prev + 1);
        return;
      }

      if (validationSource === "submit") {
        await handleSubmit(null, true);
        return;
      }

    } catch (error) {
      setValidationReason(error.message);

      setApiValidationFields(
        error.fieldName?.split(",") || []
      );

      setIsDuplicateNameValidation(false);

    } finally {
      setSubmitLoading(false);
    }
  };

  const handleValidationOk = () => {
    setOpenValidationDialog(false);

    if (apiValidationFields.length > 0) {
      const firstField = apiValidationFields[0];

      const fieldConfig = formConfig.find(f => f.fieldName === firstField);

      if (fieldConfig?.tabName) {
        const tabIndex = tabNames.findIndex(tab => tab === fieldConfig.tabName);
        if (tabIndex !== -1) {
          setActiveTab(tabIndex);
        }
      }

      setTimeout(() => {
        const input =
          document.querySelector(`input[name="${firstField}"]`) ||
          document.querySelector(`[name="${firstField}"] input`) ||
          document.querySelector(`textarea[name="${firstField}"]`);

        if (input) {
          input.focus();
          input.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    }
  };




  const breadcrumbFields =
    selectedProvider?.toUpperCase() === "AWS"
      ? ["Ec2instance", "location", "Keypair"]
      : ["TemplateOptions", "resourceGroup", "virtualnetwork"];

  const clientName = localStorage.getItem("clientName");
  const organizationName = sessionStorage.getItem('organizationName');

  useEffect(() => {
    if (!location.state?.templateId || !formConfig.length) {
      return
    }
    handleTemplateClick(location.state.templateId, location.state.templateName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formConfig.length, location.state])

  const handleTemplateClick = async (templateId, templateName) => {
    try {
      setLoading(true);
      updateTemplateLoading(
        "Fetching Template",
        `Retrieving template "${templateName}" and preparing the field mappings.`,
        40
      );
      const response = await api.get(`/api/Resources/GetResourceTemplateDetails/${templateId}`, {
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "application/json",
        },
      });

      if (response.status === 200) {
        handleTemplateSelect(response.data, templateName);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Failed to load template");
    }
  };

  useEffect(() => {
    if (isAzureStorageAccount || isAzureVirtualMachine) return;
    const OrganizationId = sessionStorage.getItem("organizationId")
    const cloudProviderId = localStorage.getItem("selectedProviderId");
    const vmSizeId = formData["VMSize"]?.value;
    const diskSizeId = formData["DiskOption"]?.value;

    if (!vmSizeId) {
      setPriceDetails(null);
      return;
    }

    const fetchPriceDetails = async () => {
      try {
        const url = `/api/AzureResource/vmPrice/${OrganizationId}/${cloudProviderId}`;
        const response = await api.get(url, {
          headers: {
            "X-CORRELATION-ID": "e7707845-953b-4baa-8dea-260aa7dfc8f9",
            accept: "text/plain",
          },
          params: {
            vmSizeId,
            diskSizeId: diskSizeId || undefined,
          },
        });
        setPriceDetails(response.data);
      } catch (error) {
        console.error("Error fetching price details:", error);
        setPriceDetails(null);
      }
    };

    if (diskSizeId !== undefined) {
      fetchPriceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData["VMSize"]?.value,
    formData["DiskOption"]?.value,
    selectedProvider,
    isAzureStorageAccount,
    isAzureVirtualMachine,
  ]);

  const handleTemplateSelect = async (templateDataArray, templateName) => {
    try {
      setLoading(true); // Keep loader on while we fetch dependent dropdowns
      updateTemplateLoading(
        "Analyzing Template",
        `Reading template "${templateName}" and extracting reusable values.`,
        50
      );
      const templateData = {};
      const restoredAdditionalTags = [];

      templateDataArray.forEach(item => {
        // 1. Handle Additional Tags
        if (item.isAdditionalTag === true) {
          const tagName = item.fieldName;
          let tagValue = item.fieldValue;

          if (typeof tagValue === 'string' && tagValue.includes('|additionalTagsTrue')) {
            tagValue = tagValue.replace('|additionalTagsTrue', '');
          }

          restoredAdditionalTags.push({
            id: crypto.randomUUID(),
            key: tagName,
            value: tagValue
          });

          return;
        }

        if (item.fieldName === "Email" || item.fieldName === "Owner") {
          return;
        }

        // 2. Skip Email (usually handled by local storage)
        if (item.fieldName === "Email") {
          return;
        }

        let value = item.fieldValue;
        let label = item.fieldData || item.fieldValue;

        // 3. Handle JSON strings/arrays from API
        if (typeof value === 'string' && value.startsWith('[')) {
          try {
            const parsedValue = JSON.parse(value);
            if (Array.isArray(parsedValue)) {
              label = parsedValue.join(", ");
              value = parsedValue;
            }
          } catch (e) {
            console.error(`Parsing error for ${item.fieldName}:`, e);
            label = value;
          }
        }

        if (typeof value === 'number') {
          value = value.toString();
        }

        // 4. CRITICAL FIX: Split multi-select strings into individual chips
        const fieldConfig = formConfig.find(f => f.fieldName === item.fieldName);
        if (fieldConfig?.fieldType === "multiSelectDropdown") {
          let valuesArray = [];
          if (Array.isArray(value)) {
            valuesArray = value.map(String);
          } else if (typeof value === "string") {
            // Split by comma and remove empty spaces/entries
            valuesArray = value.split(",").map(v => v.trim()).filter(Boolean);
          } else if (value) {
            valuesArray = [String(value)];
          }
          value = valuesArray;
          label = Array.isArray(label) ? label.join(", ") : String(label ?? "");
        }

        templateData[item.fieldName] = {
          value: value,
          label: label
        };
      });

      updateTemplateLoading(
        "Processing Template Fields",
        "Transforming template values so they match the create resource form.",
        60
      );

      // 5. Build context for dependent API calls
      if (isAzure) {
        Object.assign(templateData, getAzureFormDefaults(formConfig));
      }

      const combinedData = { ...formData, ...templateData };

      const fieldsInOrder = [
        'location',
        'environment',
        'platformDetails',
        'Ec2instance',
        'vpcId',
        'subnet',
        'AwsAmis',
        'Keypair',
        'securityGroupID',
        'Operating_system',
        'Specific_Type',
        'IvantiMachineGroup',
        'IvantiCredential',
        'IvantiDeploymentTemplate',
        'engine',
        'sqlServerEdition',
        'engine',
        'sqlServerEdition',
        'engineVersion',
        'dbInstanceType',
        'dbInstanceClassOption',
        'postgresInstanceType',
        'sqlServerInstanceType',
        'storageType',
        'dbSubnetGroupName'
      ];

      // 6. Fetch dependent dropdowns sequentially
      const templateFieldCount = fieldsInOrder.filter(fieldName => templateData[fieldName]).length || 1;
      let processedTemplateFields = 0;
      for (const fieldName of fieldsInOrder) {
        if (templateData[fieldName]) {
          const field = formConfig.find(f => f.fieldName === fieldName);
          if (field?.dropdownType === 'api') {
            processedTemplateFields += 1;
            const progress = 60 + Math.round((processedTemplateFields / templateFieldCount) * 25);
            updateTemplateLoading(
              "Fetching Template Fields",
              `Loading options for ${field.fieldLabel || field.fieldName}.`,
              progress
            );
            await fetchDropdownForField(fieldName, combinedData);
            await delay(400); // Give API time to resolve before next dependency
          }
        }
      }

      setAdditionalTags(restoredAdditionalTags);
      updateTemplateLoading(
        "Applying Template",
        "Populating the form with the template values and finalizing the screen.",
        92
      );

      // 7. Final State Update
      setFormData(prevData => {
        const email = sessionStorage.getItem("emailId") || "";

        const newData = {
          ...prevData,
          ...templateData,
          Email: {
            value: email,
            label: email
          },
          Owner: {              
            value: email,
            label: email
          }
        };

        // Sync Environment labels
        if (newData["environment"]?.value) {
          const selectedOption = dropdownData["environment"]?.find(
            (option) => option.value === newData["environment"].value
          );
          const environmentText = selectedProvider?.toUpperCase() === "AWS"
            ? (selectedOption?.text || newData["environment"].label)
            : newData["environment"].label;

          newData["Environment"] = {
            value: environmentText,
            label: environmentText
          };
        }

        // Auto-construct Resource Name based on naming standards
        if (
          namingStandard &&
          newData["ApplicationName"]?.value &&
          !isManualResourceName
        ) {
          const updatedResourceName = constructResourceName(newData, namingStandard);

          if (updatedResourceName) {
            newData["resourceName"] = {
              value: updatedResourceName,
              label: updatedResourceName
            };
            newData["Name"] = {
              value: updatedResourceName,
              label: updatedResourceName
            };
          }
        }

        if (newData["ApplicationName"]?.value) {
          const sanitizedValue = newData["ApplicationName"].value.replaceAll(/[^a-zA-Z0-9]/g, "");
          const vmName = `vm${sanitizedValue}`;
          newData["VmName"] = { value: vmName, label: vmName };
        }

        // Ensure fields populated by template are visible
        Object.keys(templateData).forEach(key => {
      const field = formConfig.find(f => f.fieldName === key);
    
      if (field?.isDisplay === true) {
        updateFieldVisibility(setFormConfig, key, true);
      }
    });

    if (newData["platformDetails"]?.value === "Windows" && resourceName?.toLowerCase().includes("ec2")) {
            updateFieldVisibility(setFormConfig, "IvantiMachineGroup", true);
            updateFieldVisibility(setFormConfig, "IvantiCredential", true);
            updateFieldVisibility(setFormConfig, "IvantiDeploymentTemplate", true);
        }

        return newData;
      });

      updateTemplateLoading(
        "Template Ready",
        `Template "${templateName}" was applied successfully.`,
        100
      );
      await delay(250);
      setLoading(false);
      toast.success(`Template "${templateName}" applied successfully!`);

    } catch (error) {
      console.error("Error applying template:", error);
      toast.error("Failed to apply template");
      setLoading(false);
    }
  };

  const valEq = (a, b) => a?.toString() === b?.toString();
  const norm = v => (v ?? "").toString().toLowerCase();
  const labelOf = o => o.label ?? o.text ?? "";

  const matchOption = (options, target) => {
    if (!target || !Array.isArray(options)) return null;
    const t = norm(target);
    return options.find(
      o =>
        valEq(o.value, target) ||
        norm(labelOf(o)) === t ||
        norm(labelOf(o)).includes(t)
    );
  };

  const applySingleValueField = (setFormDataFn, fieldName, match) => {
    if (!match) return;
    setFormDataFn(prev => ({
      ...prev,
      [fieldName]: {
        value: match.value,
        label: match.label ?? match.text,
      },
    }));
  };

  const applyMultiValueField = (setFormDataFn, fieldName, match) => {
    if (!match) return;
    setFormDataFn(prev => ({
      ...prev,
      [fieldName]: {
        value: [match.value],
        label: match.label ?? match.text,
      },
    }));
  };

  const applySidebarContext = async () => {
    if (!sidebarContext) return;

    try {
      const nextFormData = { ...formData }; // Create a local copy
      if (sidebarContext.region && Array.isArray(dropdownData.location)) {
        const loc = dropdownData.location.find(o => valEq(o.value, sidebarContext.region));
        if (loc) {
          nextFormData.location = { value: loc.value, label: loc.label ?? loc.text };
        }
      }
      if (sidebarContext.vpc) {
        const { options: vpcOptions } = await fetchDropdownForField("vpcId", nextFormData);
        const vpcMatch = matchOption(vpcOptions, sidebarContext.vpc);
        if (vpcMatch) {
          nextFormData.vpcId = { value: vpcMatch.value, label: vpcMatch.label ?? vpcMatch.text };
          const { options: subnetOptions } = await fetchDropdownForField("subnet", nextFormData);
          const subnetMatch = matchOption(subnetOptions, sidebarContext.subnet);
          if (subnetMatch) {
            nextFormData.subnet = { value: subnetMatch.value, label: subnetMatch.label ?? subnetMatch.text };
          }
          const { options: sgOptions } = await fetchDropdownForField("securityGroupID", nextFormData);
          const sgMatch = matchOption(sgOptions, sidebarContext.securityGroup);
          if (sgMatch) {
            nextFormData.securityGroupID = { value: [sgMatch.value], label: sgMatch.label ?? sgMatch.text };
          }
        }
      }
      setFormData(nextFormData);
      toast.success(" Form pre-filled from sidebar!");
    } catch (error) {
      console.error("applySidebarContext error:", error);
    }
  };
  const [forceNameError, setForceNameError] = useState(false);
  const handleEditResourceName = () => {
    setOpenValidationDialog(false);
    setActiveTab(0);
    setForceNameError(true);

    let attempts = 0;
    const tryFocus = setInterval(() => {
      attempts++;

      const input = document.querySelector('input[name="resourceName"], input[name="Name"]');

      if (input) {
        clearInterval(tryFocus);
        input.focus();

        const scrollContainer = document.getElementById('form-scroll-container');
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const inputRect = input.getBoundingClientRect();

          scrollContainer.scrollBy({
            top: inputRect.top - containerRect.top - 60,
            behavior: 'smooth'
          });
        } else {
          input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

      } else if (attempts >= 15) {
        clearInterval(tryFocus);
        const allInputs = Array.from(document.querySelectorAll('input')).map(i => i.name);
      }
    }, 100);
  };


  const azurePricingStatus = isAzureVirtualMachine ? vmPricingStatus : storagePricingStatus;
  const hasAzureMetadataPricing = isAzureStorageAccount || isAzureVirtualMachine;
  const showPricePanel =
    hasAzureMetadataPricing ? azurePricingStatus.state === "ready" : (["General", "Configuration", "Network", "Tag", "Listeners", "Networking", "Disks"].includes(tabNames[activeTab]) &&
    (
      (selectedProvider?.toUpperCase() === "AZURE" && priceDetails) ||
      (selectedProvider?.toUpperCase() === "AWS" && awsPrice)
    ));

  return (
    <Layout pageType="sideBar">
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <Typography variant="title" sx={{ ml: 2 }}>
          {resourceName} Creation
        </Typography>
      </Box>

      <Box sx={{ px: "16px" }}>

        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (themeStyle) => themeStyle.zIndex.drawer + 999,
            backgroundColor: "rgba(15, 23, 42, 0.48)",
          }}
          open={loading}
        >
          {isTemplateFlow ? (
            <Box
              sx={{
                width: "min(520px, calc(100vw - 32px))",
                backgroundColor: (t) => t.palette.mode === "dark" ? "background.default" : "#e5f6fd",
                borderRadius: 3,
                boxShadow: 8,
                px: 3,
                py: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                    color: "#fff",
                    boxShadow: "0 12px 24px rgba(37,99,235,.24)",
                  }}
                >
                  <CircularProgress size={22} thickness={5} color="inherit" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: "1.05rem" }}>
                    {loadingTitle}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.92rem", mt: 0.5 }}>
                    {loadingMessage}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", fontWeight: 600 }}>
                  Template Progress
                </Typography>
                <Typography sx={{ color: "primary.main", fontSize: "0.8rem", fontWeight: 700 }}>
                  {loadingProgress}%
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  height: 10,
                  borderRadius: 999,
                  overflow: "hidden",
                  backgroundColor: "action.hover",
                }}
              >
                <Box
                  sx={{
                    width: `${loadingProgress}%`,
                    height: "100%",
                    borderRadius: 999,
                    transition: "width .35s ease",
                    background: "linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: (t) => t.palette.mode === "dark" ? "background.default" : "#e5f6fd",
                padding: 1,
                borderRadius: "100%",
                boxShadow: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Backdrop>
        <>


          <Box sx={{ display: "flex", gap: showPricePanel ? 2 : 0 }}>
            <Box sx={{
              width: showPricePanel ? "75%" : "100%",
              transition: "width 0.6s ease",
              overflow: "hidden"
            }}>
              {/* ----------- HEADER: STEPPER CONTAINER ----------- */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 4, boxShadow: "0 10px 30px #0000001a"
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <CommonTabs
                    tabs={tabNames.map((name) => ({
                      label: name,
                    }))}
                    value={activeTab}
                    onChange={setActiveTab}
                  />
                </Box>
              </Box>
              {/* ----------- THE CONTAINER FOR THE FORM  ----------- */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100vh - 260px)",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  p: 2
                }}
              >

                {/* ----------- SCROLLABLE FORM CONTENT ----------- */}
                {hasAzureMetadataPricing && azurePricingStatus.state === "loading" && (
                  <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading pricing...</Typography>
                  </Box>
                )}
                {hasAzureMetadataPricing && azurePricingStatus.state === "error" && (
                  <Alert severity="error" sx={{ mb: 1 }}>{azurePricingStatus.message}</Alert>
                )}
                {isAzureVirtualMachine && azurePricingStatus.state === "unavailable" && (
                  <Alert severity="info" sx={{ mb: 1 }}>Pricing is unavailable for the selected VM configuration.</Alert>
                )}
                {isAzure && tabNames[activeTab] === "Summary" ? (
  <AzureSummary
    selectedProvider={selectedProvider}
    formConfig={formConfig}
    formData={formData}
    dropdownData={dropdownData}
    additionalTags={additionalTags}
  />
) : (
  <>

                <Box
                  id="form-scroll-container"
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    px: 1, pt: 0.5,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "text.disabled",
                      borderRadius: "6px",
                    },
                  }}
                >
                  {/* ----------- NORMAL TAB FIELDS ----------- */}
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: "2px",
                    }}
                  >
                    {isAzureStorageAccount ? (
                      <AzureStorageAccountCreation
                        activeTabName={tabNames[activeTab]}
                        formConfig={formConfig}
                        formData={formData}
                        renderField={renderField}
                        shouldHideField={shouldHideField}
                        renderTagsSection={renderTagsSection}
                        handleChange={handleChange}
                        disabledFields={disabledFields}
                        showValidation={formSubmitted || validatedTab === activeTab}
                      />
                    ) : isAzureVirtualMachine ? (
                      <AzureVirtualMachineCreation
                        activeTabName={tabNames[activeTab]}
                        formConfig={formConfig}
                        formData={formData}
                        renderField={renderField}
                        shouldHideField={shouldHideField}
                        renderTagsSection={renderTagsSection}
                      />
                    ) : formConfig
                      .filter((field) => {
                        const route53FieldNames = [
                          "HostedZones",
                          "RecordName",
                          "RecordType",
                          "Alias",
                          "RecordValues",
                          "TTL",
                          "RoutingPolicy",
                        ];
                        return (
                          field.tabName === tabNames[activeTab] &&
                          !field.subTab &&
                          field.fieldType !== "collection" &&
                          field.isDisplay &&
                          !(resourceName === "Route 53" && route53FieldNames.includes(field.fieldName))
                        );
                      })
                      .map((field) => {
                        const renderedField = renderField(field);
                        if (!renderedField) return null;
                        const isFullWidth = field.fieldName === "masterUsername";
                        return (
                          <Box
                            key={field.fieldId}
                            sx={{
                              flex: isFullWidth ? "1 1 100%" : "1 1 calc(50% - 16px)",
                              minWidth: "30px",
                              mr: 1,
                              mb: 2,
                            }}
                          >
                            {renderedField}
                          </Box>
                        );
                      })}

                    {!isAzureStorageAccount && resourceName === "Route 53" && tabNames[activeTab] === "General" && (
                      <Box sx={{ width: "100%", mt: 2 }}>
                        <DnsRecordForm
                          fields={formConfig}
                          formData={formData}
                          setFormData={setFormData}
                          dropdownData={dropdownData}
                          formSubmitted={formSubmitted}
                          apiValidationFields={apiValidationFields}
                          setApiValidationFields={setApiValidationFields}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* ----------- LISTENERS SECTION ----------- */}
                  {formConfig
                    .filter(
                      (field) =>
                        field.tabName === tabNames[activeTab] &&
                        field.fieldType === "collection" &&
                        field.isDisplay
                    )
                    .map((field) => {

                      if (field.fieldName === "InboundRules") {
                        return (
                          <Box key="sg-rules" sx={{ width: "100%", mt: 2 }}>
                            <SecurityGroupRulesAccordion
                              formConfig={formConfig}
                              formData={formData}
                              setFormData={setFormData}
                              formSubmitted={formSubmitted}
                              onValidationChange={({ isValid, nextError }) => {
                                setSgValid(isValid);
                                setSgNextError(nextError || "");
                              }}
                              sgTouched={sgTouched}
                              api={api}
                              apiContext={{
                                accountId: localStorage.getItem("selectedAccountId"),
                                region: formData["location"]?.value,
                                vpcId: formData["vpcId"]?.value,
                              }}
                            />
                          </Box>
                        );
                      }

                      if (field.fieldName === "OutboundRules") {
                        return null;
                      }

                      return (
                        <Box key={field.fieldId} sx={{ width: "100%", mt: 2 }}>
                          {resourceName === "NLB" ? (
                            <ListenersNLBAccordion
                              ref={listenersRef}
                              field={field}
                              formData={formData}
                              setFormData={setFormData}
                              activeAccountId={activeAccountId}
                              selectedProvider={selectedProvider}
                              userId={userId}
                              formSubmitted={formSubmitted}
                              resourceId={resourceId}
                              formConfig={formConfig}
                              dropdownData={dropdownData}
                            />
                          ) : (
                            <ListenersAlbAccordion
                              ref={listenersRef}
                              field={field}
                              formData={formData}
                              setFormData={setFormData}
                              activeAccountId={activeAccountId}
                              selectedProvider={selectedProvider}
                              userId={userId}
                              formConfig={formConfig}
                              dropdownData={dropdownData}
                              resourceId={resourceId}
                              formSubmitted={formSubmitted}
                            />
                          )}
                        </Box>
                      );
                    })}

                  {/* ----------- RDS Configuration ----------- */}
                  {resourceName?.toLowerCase().includes("rds") && tabNames[activeTab] === "Configuration" && (
                    <RdsConfiguration
                      formConfig={formConfig}
                      formData={formData}
                      setFormData={setFormData}
                      formSubmitted={formSubmitted}
                      apiValidationFields={apiValidationFields}
                      setApiValidationFields={setApiValidationFields}
                      disabledFields={disabledFields}
                      renderField={renderField}
                    />
                  )}





                  {/* ----------- SUB TABS ----------- */}
                  {!isAzureStorageAccount && !isAzureVirtualMachine && !(resourceName?.toLowerCase().includes("rds") && tabNames[activeTab] === "Configuration") && (
                    Array.from(subTabNames)
                      .filter((subTabName) => {
                        const name = subTabName.toLowerCase().trim();

                        if (name === "listeners" || name === "listener") {
                          return false;
                        }

                        if (name === "agent installation" && !resourceName?.toLowerCase().includes("ec2")) {
                          return false;
                        }

                        const hasListeners = formConfig.some(
                          (f) =>
                            f.tabName === tabNames[activeTab] &&
                            f.fieldType === "collection"
                        );

                        if (hasListeners) {
                          if (
                            name.includes("secure") ||
                            name.includes("security") ||
                            name.includes("waf") ||
                            name.includes("firewall") ||
                            name.includes("web application")
                          ) {
                            return false;
                          }
                        }

                        return true;
                      })
                      .map((subTabName) => {
                        const isGeneralSubTab =
                          subTabName?.toLowerCase().trim() === "general";

                        const visibleFieldsInSubTab = formConfig.filter(
                          (field) =>
                            field.tabName === tabNames[activeTab] &&
                            field.subTab === subTabName &&
                            field.isDisplay && 
                            !shouldHideField(field, formData)
                        );

                        if (visibleFieldsInSubTab.length === 0) {
                          return null;
                        }

                        return (
                          <Box key={subTabName} sx={{ width: "100%" }}>
                            {!isGeneralSubTab && (
                              <Typography
                                variant="h5"
                                gutterBottom
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1, mt: 2
                                }}
                              >
                                {subTabName}
                              </Typography>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                flexWrap: "wrap",
                                justifyContent: "flex-start",
                                gap: "12px",
                              }}
                            >
                              {visibleFieldsInSubTab.map((field) => {
                                  const renderedField = renderField(field);
                                  if (!renderedField) return null;
                                  const isFullWidth = field.fieldName === "masterUsername";
                                  return (
                                    <Box
                                      key={field.fieldId}
                                      sx={{
                                        flex: isFullWidth ? "1 1 100%" : "1 1 calc(50% - 16px)",
                                        minWidth: "300px",
                                        mr: 1,
                                        mb: 1,
                                      }}
                                    >
                                      {renderedField}
                                    </Box>
                                  );
                                })}
                            </Box>

                            <Box>
                              {activeTab === tabNames.indexOf("Tag") &&
                                subTabName === "General" &&
                                renderTagsSection()}
                            </Box>
                          </Box>
                        );
                      })
                  )}

                </Box>
                </>
                )}
              </Box>
              {/* ^^^ THE SCROLLABLE FORM BOX CLOSES HERE ^^^ */}
            </Box>
            {/* ----------- PRICE DETAILS ----------- */}

            <Box sx={{
              width: showPricePanel ? "25%" : "0%",
              opacity: showPricePanel ? 1 : 0,
              overflow: "hidden",
              transition: "width 0.6s ease, opacity 0.4s ease"
            }}>
              <Box
                sx={{
                  p: 3,
                  height: 'calc(100vh - 200px)',
                  overflowX: 'auto',
                  borderRadius: "12px",
                  //  Dark mode uses default paper, Light mode uses the soft blue gradient
                  background: (t) => t.palette.mode === 'dark' ? t.palette.background.paper : "linear-gradient(145deg, #f0f7ff 0%, #e3f2fd 100%)",
                  border: 1,
                  //  Dark mode uses the theme divider, Light mode keeps the custom blue border
                  borderColor: (t) => t.palette.mode === 'dark' ? 'divider' : '#e0eaf5',
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    //  Swapped hardcoded #1a2027 to text.primary for automatic contrast
                    color: "text.primary",
                    mb: 2,
                    fontSize: '1.25rem'
                  }}
                >
                  Pricing Summary
                </Typography>

                <Box sx={{ flexGrow: 1 }}>
                  {isAzureStorageAccount ? (
                    <AzureStorageAccountPricing
                      formData={formData}
                      organizationName={organizationName}
                      onPriceChange={setPriceDetails}
                      onStatusChange={setStoragePricingStatus}
                    />
                  ) : isAzureVirtualMachine ? (
                    <AzureVirtualMachinePricing
                      formData={formData}
                      formConfig={formConfig}
                      dropdownData={dropdownData}
                      organizationName={organizationName}
                      onPriceChange={setPriceDetails}
                      onStatusChange={setVmPricingStatus}
                    />
                  ) : renderPriceDetails({
                    selectedProvider,
                    priceDetails,
                    awsPrice,
                    resourceName,
                    clientName,
                    organizationName,
                  })}
                </Box>

                <Typography
                  variant="caption"
                  sx={{ mt: 3, color: 'text.secondary', fontStyle: 'italic', display: 'block' }}
                >
                  * Prices are estimates based on current selection.
                </Typography>
              </Box>
            </Box>

          </Box>
          {/* ----------- FOOTER AREA (OUTSIDE THE GREY BOX) ----------- */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2, py: 1,
              backgroundColor: (t) => t.palette.mode === "dark" ? "background.default" : "#e5f6fd",
              borderTop: "1px solid",
              width: "100%", // Spans the full width correctly
              borderColor: (t) => t.palette.mode === "dark" ? "divider" : "#eee",
            }}
          >
            {/* EXISTING ALERT IN FOOTER */}
            <Alert
              severity="info"
              icon={<InfoOutlineRounded fontSize="inherit" />}
              sx={{
                minWidth: '300px',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  fontSize: '0.85rem'
                }
              }}
            >
              <Box sx={{ flexGrow: 1, p: 0 }}>
                Mandatory fields are required only at the time of submission.
              </Box>
            </Alert>

            {/* ACTION BUTTONS */}
            <Box sx={{ display: "flex", gap: 2, height: "fit-content" }}>
              {/* Always show Cancel */}
              <Button variant="cancelContained" onClick={() => navigate(-1)}>
                Cancel
              </Button>

              {/* Show Previous if not on the first tab */}
              {activeTab > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handlePrevious}
                  disabled={submitLoading}
                >
                  Previous
                </Button>
              )}

              {activeTab < tabNames.length - 1 ? (
                <Box>
                  {/* <Button variant="contained"  lineHeight="1.75" onClick={handleNext} sx={{ minWidth: '100px' }}>
                      {submitLoading ? <CircularProgress size={20} /> : "Next"}
                    </Button>
                    */}
                  <Button
                    variant="contained"
                    onClick={handleNextWithoutValidation}
                    sx={{
                      minWidth: "100px",
                      height: "40px",
                      minHeight: "40px",
                      px: 3,
                      lineHeight: 1.75
                    }}
                  >
                    {submitLoading ? <CircularProgress size={20} /> : "Next"}
                  </Button>

                </Box>
              ) : (
               
               
                <Box sx={{ display: "flex", gap: 2, lineHeight: 1.75, }}>
                  {isAzure ? ( <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSaveTemplateOpen(true)}
                    disabled={submitLoading}
                  >
                    Save as Template
                  </Button>) :(
                   <RoleGuard requiredPermission="CREATE_TEMPLATE">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSaveTemplateOpen(true)}
                    disabled={submitLoading}
                  >
                    Save as Template
                  </Button>
                  </RoleGuard>)}
        {isAzure ? (   <Button
                    variant="greenContained"
                    onClick={handleSubmit}
                    disabled={submitLoading}
                  >
                    {submitLoading ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>):(       
 <RoleGuard requiredPermission="CREATE_RESOURCE">
                  <Button
                    variant="greenContained"
                    onClick={handleSubmit}
                    disabled={submitLoading}
                  >
                    {submitLoading ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                  </RoleGuard>
                   )}    
                </Box>
                
              )}
            </Box>
          </Box>
        </>

        <Dialog
          open={openValidationDialog}
          onClose={() => setOpenValidationDialog(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: { sx: { pb: 2, px: 1, pt: 1 } }
          }}
        >
          <DialogTitle sx={{ fontSize: "18px", fontWeight: "Bold" }}>
            Validation
          </DialogTitle>

          <DialogContent>
            {isDuplicateNameValidation ? (
              <>
                <Typography sx={{ mb: 2 }}>
                  {validationReason}
                </Typography>
                {resourceName?.toLowerCase().includes("route 53") ? <Typography>
                  Would you like to edit the record name ?
                </Typography> : <Typography>
                  Would you like to edit the resource name or continue with a suffix?
                </Typography>}
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>
                  {validationReason}
                </Typography>

                {apiValidationFields.length > 0 && (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      Please check and update the following fields.
                    </Typography>
                    <Typography sx={{ mb: 2, fontWeight: 600 }}>
                      Fields: {apiValidationFields
                        .map(name => formConfig.find(f => f.fieldName === name)?.fieldLabel || name)
                        .join(", ")}
                    </Typography>
                  </>
                )}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", gap: 2 }}>
            {isDuplicateNameValidation ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleEditResourceName()}
                >
                  {resourceName?.toLowerCase().includes("route 53") ? " Edit RecordName" : "Edit ResourceName"}
                </Button>

                {!resourceName?.toLowerCase().includes("route 53") && (<Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleContinueWithSuffix()}
                >
                  Continue with Suffix
                </Button>)}
              </>) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleValidationOk}
              >
                OK
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={openIntegrationDialog} onClose={() => setOpenIntegrationDialog(false)} maxWidth="sm"
          fullWidth slotProps={{
            paper: { sx: { pb: 2, px: 1, pt: 1 } }
          }}
        >
          <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold' }}>Integration Service Error</DialogTitle>
          <DialogContent>
            {integrationError.split("\n").map((msg) => (
              <Typography key={msg} color="error" sx={{ mb: 1 }}>
                • {msg}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button
              onClick={() => setOpenIntegrationDialog(false)} variant="contained" color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

      <SaveTemplateDialog
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        formData={formData}
        additionalTags={additionalTags}
        resourceId={resourceId}
        resourceName={resourceName}
        selectedProvider={selectedProvider}
      />

      <GetAllTemplatesDialog
        open={getAllTemplatesOpen}
        onClose={() => setGetAllTemplatesOpen(false)}
        onTemplateSelect={handleTemplateSelect}
        resourceId={resourceId}
        selectedProvider={selectedProviderId}
      />
{isAzure ? (
  <AzureSubmissionProgressPopup
    open={submissionOpen}
    progress={progress}
    steps={steps}
    successMessage={successMessage}
  />
) : (
      <ProgressPopup
        open={submissionOpen}
        progress={progress}
        steps={steps}
        successMessage={successMessage}
      />
)}
    </Layout>
  );
};

export default CreateResource;
